import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  Search, 
  Edit, 
  Save, 
  Trash2, 
  X, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  Shield,
  GripVertical,
  Clock,
  User,
  Users,
  Monitor,
  Star,
  Folder,
  FolderOpen,
  Plus,
  FileDown,
  Loader2,
  Check,
  Copy,
  Target,
  Sparkles,
  ChevronDown,
  RefreshCw,
  BarChart2
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import toast from 'react-hot-toast'
import EditableText from '../components/EditableText'
import EditableNumber from '../components/EditableNumber'
import ToggleSwitch from '../components/ToggleSwitch'
import EditableSelect from '../components/EditableSelect'
import EditableDate from '../components/EditableDate'
import CompetencySelector, { CompetencyAssignment } from '../components/CompetencySelector'
import TaskCompetencyModal from '../components/TaskCompetencyModal'
import { apiClient } from '../lib/api'
import {
  getBausteinViewMode,
  setBausteinViewMode,
  type LrBausteinViewMode
} from '../lib/lrUiPreferences'
import { useAutoSave } from '../hooks/useAutoSave'
import {
  BausteinTile,
  Badge,
  BreadcrumbBar,
  Button,
  CollapsibleTile,
  EmptyTableRow,
  FolderTile,
  ListToolbar,
  SortableTh,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea,
  Tile,
  TileDescription,
  TileMain,
  TileRow,
  TileTitle,
  ViewModeToggle
} from '../components/ui'

// Hilfsfunktion für API-URLs
const getApiUrl = (endpoint: string) => {
  const baseUrl = apiClient.getBaseUrl()
  return `${baseUrl}${endpoint}`
}

const bbLabelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
const bbInputClass = 'input-field'
const bbTextareaClass = 'input-field min-h-[120px] resize-y'
const bbMutedPanelClass = 'rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60'
const bbAccentBluePanel = 'rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30'
const bbInfoBlueBox = 'mb-3 text-sm text-gray-600 dark:text-gray-300 bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-950/30 dark:border-blue-800'
const bbCurriculumPickerBtn =
  'w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg bg-white hover:bg-blue-50 hover:border-blue-400 text-left flex items-center justify-between transition-all duration-200 group dark:border-blue-700 dark:bg-gray-900 dark:hover:bg-gray-800 dark:hover:border-blue-500'
const bbCurriculumDropdown = 'border-2 border-blue-200 rounded-xl bg-white shadow-lg overflow-hidden dark:border-gray-600 dark:bg-gray-900'
const bbCurriculumDropdownHeader =
  'p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 dark:from-gray-800 dark:to-gray-900 dark:border-gray-700'
const bbDashedBluePanel =
  'mt-4 rounded-lg border border-dashed border-blue-200 bg-blue-50/60 p-3 dark:border-blue-800 dark:bg-blue-950/20'
const bbDashedRedPanel =
  'mt-4 rounded-lg border border-dashed border-red-200 bg-red-50/60 p-3 dark:border-red-900 dark:bg-red-950/20'
const bbTaskFieldClass = 'text-xs text-gray-800 dark:text-gray-200'
const bbTasksSection =
  'rounded-lg border border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 p-4 dark:border-purple-900 dark:from-purple-950/40 dark:to-pink-950/40'
const folderIndentClasses = ['pl-2', 'pl-5', 'pl-8', 'pl-11', 'pl-14', 'pl-16']


const getAxiosErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const res = (error as { response?: { status?: number; data?: { error?: string; message?: string } } }).response
    if (res?.status) {
      const detail = res.data?.message || res.data?.error
      return detail ? `HTTP ${res.status}: ${detail}` : `HTTP ${res.status}: ${fallback}`
    }
  }
  return error instanceof Error ? error.message : fallback
}

const LERNPLAN_DATE_RANGE_ERROR = 'Das Enddatum darf nicht vor dem Startdatum liegen.'

function isLernplanEndBeforeStart(startDatum: string, endDatum: string): boolean {
  const start = startDatum?.trim().slice(0, 10)
  const end = endDatum?.trim().slice(0, 10)
  if (!start || !end || !/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return false
  }
  return end < start
}


// Sortable Lernplan Komponente
function SortableLernplan({ 
  lernplan, 
  onEdit, 
  onUpdateSortierung,
  onExport,
  onExportSimple,
  onUpdateLernplan,
  onDelete,
  onClone,
  perfStats
}: { 
  lernplan: Lernplan
  onEdit: (lernplan: Lernplan) => void
  onUpdateSortierung: (lpid: string, newSortierung: number) => Promise<void>
  onExport: (lpid: string) => void
  onExportSimple: (lpid: string) => void
  onUpdateLernplan: (lpid: string, field: string, value: any) => Promise<void>
  onDelete: (lpid: string) => void
  onClone: (lpid: string) => void
  perfStats?: LernplanPerf | null
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(lernplan.LPID) })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      // Handle ISO-String format (YYYY-MM-DDTHH:mm:ss.sssZ) - extract date part only
      let datePart = dateString
      if (dateString.includes('T')) {
        datePart = dateString.split('T')[0]
      }
      
      // Handle pure date format (YYYY-MM-DD)
      const parts = datePart.split('-')
      if (parts.length === 3 && parts[0].length === 4) {
        // Format: YYYY-MM-DD -> DD.MM.YYYY
        const day = parts[2].padStart(2, '0')
        const month = parts[1].padStart(2, '0')
        const year = parts[0]
        return `${day}.${month}.${year}`
      }
      
      // Fallback: return as-is if format is unexpected
      return dateString
    } catch {
      return dateString
    }
  }

  const getDiffColor = (diff: string) => {
    switch (diff) {
      case 'GK':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200'
      case 'EK':
        return 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200'
      case 'FK':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-200'
      case 'DZ':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
    }
  }

  return (
    <Tile
      ref={setNodeRef}
      style={style}
      variant="surface"
      className="transition-all hover:shadow-md"
    >
      <div className="flex items-center space-x-4">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-2 hover:bg-gray-100 rounded"
        >
          <GripVertical className="h-5 w-5 text-gray-400" />
        </div>

        {/* Lernplan Info */}
        <div className="flex-1 grid grid-cols-6 gap-4 items-center">
          <div className="col-span-2">
            <TileTitle className="text-base">{lernplan.NAME}</TileTitle>
            <TileDescription>ID: {lernplan.LPID}</TileDescription>
          </div>
          
          <div className="text-center">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {lernplan.GESAMT_ZEITBEDARF} Min
            </span>
          </div>
          
          <div className="text-center">
            <Badge variant="info">{lernplan.AUFGABEN_ANZAHL} Aufgaben</Badge>
          </div>
          
          <div className="text-center">
            <EditableDate
              value={lernplan.START_DATUM || ''}
              onSave={(value) => onUpdateLernplan(lernplan.LPID, 'START_DATUM', value)}
              placeholder="Start Datum"
              className="text-sm text-gray-600 dark:text-gray-300"
            />
          </div>
          
          <div className="text-center">
            <EditableDate
              value={lernplan.END_DATUM || ''}
              onSave={(value) => onUpdateLernplan(lernplan.LPID, 'END_DATUM', value)}
              placeholder="End Datum"
              className="text-sm text-gray-600 dark:text-gray-300"
            />
          </div>
        </div>

        {/* Badges: Diff + Performance Stats */}
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDiffColor(lernplan.Diff)}`}>
            {lernplan.Diff || 'GK'}
          </span>
          {perfStats && (perfStats.access_count > 0 || perfStats.student_count > 0) && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 dark:border-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200"
              title={perfStats.access_count > 0
                ? `${perfStats.student_count} von ${perfStats.access_count} Schülern haben mindestens eine Aufgabe bearbeitet (Stand: ${new Date(perfStats.calculated_at).toLocaleDateString('de-DE')})`
                : `${perfStats.student_count} Schüler haben mindestens eine Aufgabe bearbeitet (Stand: ${new Date(perfStats.calculated_at).toLocaleDateString('de-DE')})`}
            >
              <Users className="h-3 w-3" />
              {perfStats.access_count > 0
                ? `${perfStats.student_count}/${perfStats.access_count}`
                : perfStats.student_count}
            </span>
          )}
          {perfStats && (perfStats.access_count > 0 || perfStats.student_count > 0) && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200 dark:border-green-800 dark:bg-green-950/50 dark:text-green-200"
              title={perfStats.access_count > 0
                ? `Ø Bearbeitungsgrad über alle ${perfStats.access_count} Schüler mit Zugang (Stand: ${new Date(perfStats.calculated_at).toLocaleDateString('de-DE')})`
                : `Ø Bearbeitungsgrad (Stand: ${new Date(perfStats.calculated_at).toLocaleDateString('de-DE')})`}
            >
              <BarChart2 className="h-3 w-3" />
              {Number(perfStats.avg_completion_pct).toFixed(0)}%
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1">
          {/* Export Button */}
          <button
            onClick={() => onExport(lernplan.LPID)}
            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Lernplan exportieren"
          >
            <FileDown className="h-5 w-5" />
          </button>
          
          {/* Simple Export Button */}
          <button
            onClick={() => onExportSimple(lernplan.LPID)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Lernplan exportieren (schlicht)"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          
          {/* Clone Button */}
          <button
            onClick={() => onClone(lernplan.LPID)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Lernplan klonen"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          
          {/* Edit Button */}
          <button
            onClick={() => onEdit(lernplan)}
            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="Lernplan bearbeiten"
          >
            <Edit className="h-5 w-5" />
          </button>
          
          {/* Delete Button */}
          <button
            onClick={() => onDelete(lernplan.LPID)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Lernplan löschen"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>
    </Tile>
  )
}

// Interfaces

interface Baustein {
  BID: number
  NAME: string
  FACH: string
  STUFE: string
  JAHR: string
  START_DATUM: string
  END_DATUM: string
  /** Aggregiert aus enthaltenen Lernplänen (MIN Start / MAX Ende) */
  ZEITFENSTER_START?: string | null
  ZEITFENSTER_END?: string | null
  /** Mindestens ein Lernplan liegt im heutigen Datumsfenster */
  HAT_AKTIVE_LERNPLAENE?: boolean | number
  INFO: string
  Eigentuemer: string
  folder_id?: number | null
  folder_name?: string | null
  COMPETENCY_COUNT: number
  curriculumIds?: number[]
  pinned?: boolean
}

interface BausteinFolder {
  id: number
  parent_id: number | null
  name: string
  sortierung: number
  bausteine_count?: number
  children?: BausteinFolder[]
}

const getBausteinZeitfensterStart = (baustein: Baustein) =>
  baustein.ZEITFENSTER_START || ''

const getBausteinZeitfensterEnd = (baustein: Baustein) =>
  baustein.ZEITFENSTER_END || ''

const bausteinHatAktiveLernplaene = (baustein: Baustein) =>
  Number(baustein.HAT_AKTIVE_LERNPLAENE) === 1

const flattenFolders = (folders: BausteinFolder[]): BausteinFolder[] => {
  const result: BausteinFolder[] = []
  const walk = (nodes: BausteinFolder[]) => {
    nodes.forEach((node) => {
      result.push(node)
      if (node.children && node.children.length > 0) {
        walk(node.children)
      }
    })
  }
  walk(folders)
  return result
}

const findFolderById = (folders: BausteinFolder[], id: number | null): BausteinFolder | null => {
  if (id == null) return null
  const all = flattenFolders(folders)
  return all.find((folder) => folder.id === id) ?? null
}

const getFolderBreadcrumb = (folders: BausteinFolder[], id: number | null) => {
  const map = new Map(flattenFolders(folders).map((folder) => [folder.id, folder]))
  const trail: Array<{ id: number | null; label: string }> = [{ id: null, label: 'Alle Bausteine' }]
  let current = id ? map.get(id) : null
  const stack: BausteinFolder[] = []
  while (current) {
    stack.push(current)
    current = current.parent_id ? map.get(current.parent_id) : null
  }
  stack.reverse().forEach((folder) => trail.push({ id: folder.id, label: folder.name }))
  return trail
}

const getCurriculumId = (curriculum: any): number => {
  if (curriculum === null || curriculum === undefined) {
    return 0
  }

  if (typeof curriculum === 'number') {
    return Number.isInteger(curriculum) && curriculum > 0 ? curriculum : 0
  }

  if (typeof curriculum === 'string') {
    const parsed = Number(curriculum)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 0
  }

  if (typeof curriculum === 'object') {
    const candidate =
      (curriculum as any).id ??
      (curriculum as any).ID ??
      (curriculum as any).curriculum_id ??
      (curriculum as any).cid

    const parsed = Number(candidate)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 0
  }

  return 0
}

const normalizeCurriculumIds = (ids: any): number[] => {
  if (!Array.isArray(ids)) return []
  const unique = new Set<number>()
  ids.forEach((value) => {
    const id = getCurriculumId(value)
    if (id > 0) {
      unique.add(id)
    }
  })
  return Array.from(unique).sort((a, b) => a - b)
}

const arraysEqual = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false
  }
  return true
}

interface Klasse {
  Klasse: string
}

interface Zuordnung {
  BID: string
  Klasse: string
}

interface Lernplan {
  LPID: string
  NAME: string
  BESCHREIBUNG?: string
  ERSTELLT_AM?: string
  ERSTELLT_VON: string
  AUFGABEN_ANZAHL: number
  GESAMT_ZEITBEDARF: number
  START_DATUM: string
  END_DATUM: string
  Sortierung: number
  Diff: string
  STATUS: number
  FACH: string
  STUFE: string
  JAHR: string
  LERNZIELE: string
  BID: number
  Eigentuemer: string
  BAUSTEIN_NAME?: string
}

interface LernplanDetail {
  LPID: number
  NAME: string
  FACH: string
  STUFE: string
  JAHR: string
  STATUS: number
  LERNZIELE: string
  END_DATUM: string
  START_DATUM: string
  BID: number
  Diff: string
}

interface Task {
  AID: number
  LPID: number
  Kurzberzeichnung: string
  Zeitbedarf: number
  Aufgabenstellung: string
  Position: number
  Einzelarbeit: boolean
  Partnerarbeit: boolean
  Gruppenarbeit: boolean
  PCTABLET: boolean
  Zusatzaufgabe: number
  Schwer: boolean
  Link: string
  Sortierung: number
  SForm: string
  HMittel: string
  Info: string
}

type ActivityType = 'building_block' | 'learning_plan' | 'task'

interface AssignmentDetailBase {
  assignment_id: number
  competency_id: number
  weight: number
  required_for_competency?: number | boolean
  auto_assigned?: number | boolean
  assignment_source: string
  notes?: string
  updated_at?: string
  created_at?: string
}

interface BuildingBlockAssignmentDetail extends AssignmentDetailBase {
  activity_id: number
}

interface LearningPlanAssignmentDetail extends AssignmentDetailBase {
  lpid: number
  lernplan_name: string
  sortierung?: number
}

interface TaskAssignmentDetail extends AssignmentDetailBase {
  aid: number
  lpid: number
  lernplan_name: string
  task_name: string
  sortierung?: number
}

interface CompetencyOriginDetailData {
  buildingBlock: BuildingBlockAssignmentDetail | null
  learningPlans: LearningPlanAssignmentDetail[]
  tasks: TaskAssignmentDetail[]
}

interface BausteinPerf {
  BID: number
  student_count: number
  access_count: number
  avg_completion_pct: number
  calculated_at: string
}

interface LernplanPerf {
  LPID: number
  BID: number
  student_count: number
  access_count: number
  avg_completion_pct: number
  calculated_at: string
}

interface AufgabePerf {
  AID: number
  LPID: number
  BID: number
  student_count: number
  access_count: number
  avg_completion_pct: number
  calculated_at: string
}

interface PerformanceStatsData {
  bausteine: BausteinPerf[]
  lernplaene: LernplanPerf[]
  aufgaben: AufgabePerf[]
}

type SortField = 'FACH' | 'JAHR' | 'COMPETENCY_COUNT' | 'STUFE' | 'NAME' | 'START_DATUM' | 'END_DATUM' | 'Sortierung' | 'GESAMT_ZEITBEDARF' | 'AUFGABEN_ANZAHL' | 'PERF_STUDENT_COUNT' | 'PERF_AVG_PCT'
type SortDirection = 'asc' | 'desc'

// SortableTask Komponente für Drag & Drop
function SortableTask({ task, onUpdateTask, onDeleteTask, deletingTask, onShowCompetencies, competencyRefreshKey, perfStats }: { 
  task: Task, 
  onUpdateTask: (taskId: number, field: string, value: any) => Promise<void>,
  onDeleteTask: (taskId: number) => Promise<void>,
  deletingTask: number | null,
  onShowCompetencies: (taskId: number) => void,
  competencyRefreshKey: number,
  perfStats?: AufgabePerf | null
}) {
  const [taskCompetencies, setTaskCompetencies] = useState<any[]>([])
  const [loadingCompetencies, setLoadingCompetencies] = useState(false)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.AID })

  // Lade Kompetenzen für diese Aufgabe
  useEffect(() => {
    const fetchCompetencies = async () => {
      try {
        setLoadingCompetencies(true)
        const response = await apiClient.get(`/competency-assignments/task/${task.AID}`)
        if (response.data.success) {
          setTaskCompetencies(response.data.data || [])
        }
      } catch (error: any) {
        console.error('Error loading task competencies:', error)
      } finally {
        setLoadingCompetencies(false)
      }
    }
    fetchCompetencies()
  }, [task.AID, competencyRefreshKey])

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  // Auto-save hooks for different fields
  const { debouncedSave: saveKurzberzeichnung } = useAutoSave(
    (value: string) => onUpdateTask(task.AID, 'Kurzberzeichnung', value),
    { toastMessage: 'Titel gespeichert' }
  )

  const { debouncedSave: saveAufgabenstellung } = useAutoSave(
    (value: string) => onUpdateTask(task.AID, 'Aufgabenstellung', value),
    { toastMessage: 'Beschreibung gespeichert' }
  )

  const { debouncedSave: saveZeitbedarf } = useAutoSave(
    (value: number | null) => onUpdateTask(task.AID, 'Zeitbedarf', value),
    { toastMessage: 'Zeitbedarf gespeichert' }
  )

  // Wrapper function to handle EditableSelect's string | number | null type
  const handleZeitbedarfSave = async (value: string | number | null) => {
    const numericValue = typeof value === 'string' ? (value === '' ? null : Number(value)) : value
    await saveZeitbedarf(numericValue)
  }

  const { debouncedSave: saveLink } = useAutoSave(
    (value: string) => onUpdateTask(task.AID, 'Link', value),
    { toastMessage: 'Link gespeichert' }
  )

  const { debouncedSave: saveInfo } = useAutoSave(
    (value: string) => onUpdateTask(task.AID, 'Info', value),
    { toastMessage: 'Info gespeichert' }
  )

  const { debouncedSave: saveHMittel } = useAutoSave(
    (value: string) => onUpdateTask(task.AID, 'HMittel', value),
    { toastMessage: 'Hilfsmittel gespeichert' }
  )


  // Determine background color based on social form and difficulty
  const getBackgroundColor = () => {
    const isHard = task.Schwer
    const socialForms = [task.Einzelarbeit, task.Partnerarbeit, task.Gruppenarbeit]
    const activeForms = socialForms.filter(Boolean).length
    
    // Multiple social forms - use gradient
    if (activeForms > 1) {
      if (task.Einzelarbeit && task.Partnerarbeit && task.Gruppenarbeit) {
        // All three forms
        return isHard 
          ? 'bg-gradient-to-r from-blue-100 via-green-100 to-red-100 border-blue-300 dark:from-blue-900/50 dark:via-green-900/50 dark:to-red-900/50 dark:border-blue-700' 
          : 'bg-gradient-to-r from-blue-50 via-green-50 to-red-50 border-blue-200 dark:from-blue-950/40 dark:via-green-950/40 dark:to-red-950/40 dark:border-blue-800'
      } else if (task.Einzelarbeit && task.Partnerarbeit) {
        // Einzel + Partner
        return isHard 
          ? 'bg-gradient-to-r from-blue-100 to-green-100 border-blue-300 dark:from-blue-900/50 dark:to-green-900/50 dark:border-blue-700' 
          : 'bg-gradient-to-r from-blue-50 to-green-50 border-blue-200 dark:from-blue-950/40 dark:to-green-950/40 dark:border-blue-800'
      } else if (task.Einzelarbeit && task.Gruppenarbeit) {
        // Einzel + Gruppe
        return isHard 
          ? 'bg-gradient-to-r from-blue-100 to-red-100 border-blue-300 dark:from-blue-900/50 dark:to-red-900/50 dark:border-blue-700' 
          : 'bg-gradient-to-r from-blue-50 to-red-50 border-blue-200 dark:from-blue-950/40 dark:to-red-950/40 dark:border-blue-800'
      } else if (task.Partnerarbeit && task.Gruppenarbeit) {
        // Partner + Gruppe
        return isHard 
          ? 'bg-gradient-to-r from-green-100 to-red-100 border-green-300 dark:from-green-900/50 dark:to-red-900/50 dark:border-green-700' 
          : 'bg-gradient-to-r from-green-50 to-red-50 border-green-200 dark:from-green-950/40 dark:to-red-950/40 dark:border-green-800'
      }
    }
    
    // Single social form
    if (task.Einzelarbeit) {
      return isHard ? 'bg-blue-100 border-blue-300 dark:bg-blue-900/60 dark:border-blue-700' : 'bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800'
    }
    if (task.Partnerarbeit) {
      return isHard ? 'bg-green-100 border-green-300 dark:bg-green-900/60 dark:border-green-700' : 'bg-green-50 border-green-200 dark:bg-green-950/40 dark:border-green-800'
    }
    if (task.Gruppenarbeit) {
      return isHard ? 'bg-red-100 border-red-300 dark:bg-red-900/60 dark:border-red-700' : 'bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-800'
    }
    return isHard ? 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600' : 'bg-white border-gray-200 dark:bg-gray-900/50 dark:border-gray-700'
  }

  // Determine handle color based on social form and difficulty
  const getHandleColor = () => {
    const isHard = task.Schwer
    const socialForms = [task.Einzelarbeit, task.Partnerarbeit, task.Gruppenarbeit]
    const activeForms = socialForms.filter(Boolean).length
    
    // Multiple social forms - use gradient handle
    if (activeForms > 1) {
      if (task.Einzelarbeit && task.Partnerarbeit && task.Gruppenarbeit) {
        // All three forms
        return isHard 
          ? 'bg-gradient-to-b from-blue-700 via-green-700 to-red-700 hover:from-blue-600 hover:via-green-600 hover:to-red-600' 
          : 'bg-gradient-to-b from-blue-600 via-green-600 to-red-600 hover:from-blue-500 hover:via-green-500 hover:to-red-500'
      } else if (task.Einzelarbeit && task.Partnerarbeit) {
        // Einzel + Partner
        return isHard 
          ? 'bg-gradient-to-b from-blue-700 to-green-700 hover:from-blue-600 hover:to-green-600' 
          : 'bg-gradient-to-b from-blue-600 to-green-600 hover:from-blue-500 hover:to-green-500'
      } else if (task.Einzelarbeit && task.Gruppenarbeit) {
        // Einzel + Gruppe
        return isHard 
          ? 'bg-gradient-to-b from-blue-700 to-red-700 hover:from-blue-600 hover:to-red-600' 
          : 'bg-gradient-to-b from-blue-600 to-red-600 hover:from-blue-500 hover:to-red-500'
      } else if (task.Partnerarbeit && task.Gruppenarbeit) {
        // Partner + Gruppe
        return isHard 
          ? 'bg-gradient-to-b from-green-700 to-red-700 hover:from-green-600 hover:to-red-600' 
          : 'bg-gradient-to-b from-green-600 to-red-600 hover:from-green-500 hover:to-red-500'
      }
    }
    
    // Single social form
    if (task.Einzelarbeit) {
      return isHard 
        ? 'bg-blue-700 hover:bg-blue-600' 
        : 'bg-blue-600 hover:bg-blue-500'
    }
    if (task.Partnerarbeit) {
      return isHard 
        ? 'bg-green-700 hover:bg-green-600' 
        : 'bg-green-600 hover:bg-green-500'
    }
    if (task.Gruppenarbeit) {
      return isHard 
        ? 'bg-red-700 hover:bg-red-600' 
        : 'bg-red-600 hover:bg-red-500'
    }
    return isHard 
      ? 'bg-gray-700 hover:bg-gray-600' 
      : 'bg-gray-600 hover:bg-gray-500'
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${getBackgroundColor()} rounded-lg mb-3 shadow-sm hover:shadow-md transition-all ${
        isDragging ? 'shadow-lg' : ''
      } ${
        task.Zusatzaufgabe === 1 
          ? 'ring-2 ring-yellow-300 ring-opacity-50 shadow-lg shadow-yellow-200/50 dark:ring-yellow-600/60 dark:shadow-yellow-900/30' 
          : ''
      }`}
    >
      <div className="flex items-stretch space-x-3">
        <div
          {...attributes}
          {...listeners}
          className={`flex-shrink-0 cursor-grab active:cursor-grabbing w-8 flex items-center justify-center ${getHandleColor()} rounded-l-lg transition-colors ${
            task.Zusatzaufgabe === 1 
              ? 'bg-gradient-to-b from-yellow-200 to-yellow-300 hover:from-yellow-300 hover:to-yellow-400' 
              : ''
          }`}
        >
          <GripVertical className={`h-5 w-5 ${
            task.Zusatzaufgabe === 1 
              ? 'text-yellow-600 hover:text-yellow-700' 
              : 'text-white hover:text-gray-200'
          }`} />
        </div>
        <div className="flex-1 min-w-0 p-4">
          {/* Header with title and time */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1 mr-4">
              <EditableText
                value={task.Kurzberzeichnung || ''}
                onSave={saveKurzberzeichnung}
                maxLength={16}
                placeholder="Aufgabentitel eingeben"
                className="text-lg font-bold text-gray-900 dark:text-gray-50"
              />
            </div>
            {perfStats && (perfStats.access_count > 0 || perfStats.student_count > 0) && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 flex-shrink-0 dark:bg-indigo-950/50 dark:text-indigo-200 dark:border-indigo-700"
                title={perfStats.access_count > 0
                  ? `${perfStats.student_count} von ${perfStats.access_count} Schülern haben diese Aufgabe bearbeitet (Stand: ${new Date(perfStats.calculated_at).toLocaleDateString('de-DE')})`
                  : `${perfStats.student_count} Schüler haben diese Aufgabe bearbeitet (Stand: ${new Date(perfStats.calculated_at).toLocaleDateString('de-DE')})`}
              >
                <Users className="h-3 w-3" />
                {perfStats.access_count > 0
                  ? `${perfStats.student_count}/${perfStats.access_count}`
                  : perfStats.student_count}
                <span className="ml-0.5 font-semibold">
                  {Number(perfStats.avg_completion_pct).toFixed(0)}%
                </span>
              </span>
            )}
          </div>

          {/* Description */}
          <div className="mb-3">
            <EditableText
              value={task.Aufgabenstellung || ''}
              onSave={saveAufgabenstellung}
              maxLength={2000}
              placeholder="Aufgabenstellung eingeben"
              multiline={true}
              className="text-base font-medium text-gray-800 dark:text-gray-100 leading-relaxed"
            />
          </div>

          {/* Toggle Switches */}
          <div className="flex flex-wrap items-center gap-4">
            <ToggleSwitch
              label="Einzelarbeit"
              value={task.Einzelarbeit}
              onToggle={(value) => onUpdateTask(task.AID, 'Einzelarbeit', value)}
            />
            <ToggleSwitch
              label="Partnerarbeit"
              value={task.Partnerarbeit}
              onToggle={(value) => onUpdateTask(task.AID, 'Partnerarbeit', value)}
            />
            <ToggleSwitch
              label="Gruppenarbeit"
              value={task.Gruppenarbeit}
              onToggle={(value) => onUpdateTask(task.AID, 'Gruppenarbeit', value)}
            />
            <ToggleSwitch
              label="PC/Tablet"
              value={task.PCTABLET}
              onToggle={(value) => onUpdateTask(task.AID, 'PCTABLET', value)}
            />
            <ToggleSwitch
              label="Schwer"
              value={task.Schwer}
              onToggle={(value) => onUpdateTask(task.AID, 'Schwer', value)}
            />
            <ToggleSwitch
              label="Zusatz"
              value={task.Zusatzaufgabe === 1}
              onToggle={(value) => onUpdateTask(task.AID, 'Zusatzaufgabe', value ? 1 : 0)}
            />
          </div>

          {/* Kompetenzen Chips */}
          {taskCompetencies.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-start gap-2 flex-wrap">
                {taskCompetencies.map(comp => (
                  <div
                    key={comp.id}
                    className={`group relative inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                      comp.auto_assigned 
                        ? 'bg-purple-50 border border-purple-200 dark:bg-purple-950/50 dark:border-purple-700' 
                        : 'bg-blue-50 border border-blue-200 dark:bg-blue-950/50 dark:border-blue-700'
                    }`}
                    title={comp.notes || comp.competency_description || ''}
                  >
                    {comp.auto_assigned && comp.confidence_score && (
                      <div className="relative">
                        <Target className="h-3 w-3 text-purple-600" />
                        <span className="absolute -top-1 -right-1 text-[8px] font-bold text-purple-700">
                          {Math.round(comp.confidence_score)}%
                        </span>
                      </div>
                    )}
                    <span className={`font-medium ${
                      comp.auto_assigned ? 'text-purple-900 dark:text-purple-200' : 'text-blue-900 dark:text-blue-200'
                    }`}>
                      {comp.competency_name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional fields and Status Icons in one row */}
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
            <div className="flex items-start justify-between gap-4">
              {/* Additional fields - 2x2 Grid */}
              <div className="grid grid-cols-2 gap-4 flex-1">
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Zeit:</label>
                  <EditableSelect
                    value={task.Zeitbedarf}
                    onSave={handleZeitbedarfSave}
                    options={[
                      { value: null, label: 'Surprise' },
                      { value: 5, label: '5 Min' },
                      { value: 10, label: '10 Min' },
                      { value: 15, label: '15 Min' },
                      { value: 30, label: '30 Min' },
                      { value: 60, label: '60+ Min' }
                    ]}
                    placeholder="Zeit"
                    className={bbTaskFieldClass}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Link:</label>
                  <EditableText
                    value={task.Link || ''}
                    onSave={saveLink}
                    maxLength={256}
                    placeholder="Link eingeben"
                    className={bbTaskFieldClass}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Hilfsmittel:</label>
                  <EditableText
                    value={task.HMittel || ''}
                    onSave={saveHMittel}
                    maxLength={255}
                    placeholder="Hilfsmittel eingeben"
                    className={bbTaskFieldClass}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Info:</label>
                  <EditableText
                    value={task.Info || ''}
                    onSave={saveInfo}
                    maxLength={256}
                    placeholder="Zusätzliche Informationen"
                    className={bbTaskFieldClass}
                  />
                </div>
              </div>
              
              {/* Status Icons und Delete Button */}
              <div className="flex items-center space-x-2 relative z-10">
                <div className="grid grid-cols-3 gap-2">
                  {task.Einzelarbeit && (
                    <div className="h-8 w-8 bg-indigo-500 rounded-full flex items-center justify-center" title="Einzelarbeit">
                      <span className="text-white text-sm font-bold">1</span>
                    </div>
                  )}
                  {task.Partnerarbeit && (
                    <div className="h-8 w-8 bg-green-500 rounded-full flex items-center justify-center" title="Partnerarbeit">
                      <span className="text-white text-sm font-bold">2</span>
                    </div>
                  )}
                  {task.Gruppenarbeit && (
                    <div className="h-8 w-8 bg-purple-500 rounded-full flex items-center justify-center" title="Gruppenarbeit">
                      <span className="text-white text-sm font-bold">3+</span>
                    </div>
                  )}
                  {task.PCTABLET && (
                    <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center" title="PC/Tablet">
                      <span className="text-white text-sm font-bold">💻</span>
                    </div>
                  )}
                  {task.Schwer && (
                    <div className="h-8 w-8 bg-red-500 rounded-full flex items-center justify-center" title="Schwer">
                      <span className="text-white text-sm font-bold">!</span>
                    </div>
                  )}
                  {task.Zusatzaufgabe === 1 && (
                    <div className="h-8 w-8 bg-yellow-500 rounded-full flex items-center justify-center" title="Zusatzaufgabe">
                      <Star className="h-5 w-5 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Competency Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onShowCompetencies(task.AID)
                  }}
                  className="p-2 text-blue-500 hover:text-blue-300 hover:bg-blue-50 rounded-lg transition-colors relative z-10 dark:hover:bg-blue-950/50"
                  title="Kompetenzen zuordnen"
                  type="button"
                >
                  <Target className="h-4 w-4" />
                </button>
                
                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onDeleteTask(task.AID)
                  }}
                  disabled={deletingTask === task.AID}
                  className="p-2 text-red-500 hover:text-red-300 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative z-10 dark:hover:bg-red-950/50"
                  title="Aufgabe löschen"
                  type="button"
                >
                  {deletingTask === task.AID ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CompetencyOriginModalProps {
  isOpen: boolean
  onClose: () => void
  competencyName: string
  detail: CompetencyOriginDetailData | null
  loading: boolean
  updateKey: string | null
  onUpdateWeight: (activityType: ActivityType, activityId: number, newWeight: number) => Promise<void>
}

function CompetencyOriginModal({
  isOpen,
  onClose,
  competencyName,
  detail,
  loading,
  updateKey,
  onUpdateWeight
}: CompetencyOriginModalProps) {
  const [weights, setWeights] = useState<Record<string, number>>({})

  useEffect(() => {
    if (detail) {
      const initial: Record<string, number> = {}
      if (detail.buildingBlock) {
        initial[`building_block-${detail.buildingBlock.activity_id}`] = clampWeight(detail.buildingBlock.weight)
      }
      detail.learningPlans.forEach(lp => {
        initial[`learning_plan-${lp.lpid}`] = clampWeight(lp.weight)
      })
      detail.tasks.forEach(task => {
        initial[`task-${task.aid}`] = clampWeight(task.weight)
      })
      setWeights(initial)
    } else {
      setWeights({})
    }
  }, [detail])

  if (!isOpen) return null

  const sourceLabels: Record<string, string> = {
    manual: 'Manuell',
    llm_direct: 'LLM direkt',
    llm_fallback_lernplan: 'LLM (Lernplan)',
    llm_fallback_baustein: 'LLM (Baustein)',
    bottom_up_task: 'Bottom-Up (Aufgabe)',
    bottom_up_learning_plan: 'Bottom-Up (Lernplan)',
    top_down_building_block: 'Top-Down (Baustein)',
    top_down_task: 'Top-Down (Aufgabe)'
  }

  const handleSliderChange = (key: string, value: number) => {
    setWeights(prev => ({
      ...prev,
      [key]: clampWeight(value)
    }))
  }

  const handleSaveWeight = async (activityType: ActivityType, activityId: number, key: string, fallback: number) => {
    const value = clampWeight(weights[key] ?? fallback)
    await onUpdateWeight(activityType, activityId, value)
  }

  const renderAssignmentRow = (
    activityType: ActivityType,
    activityId: number,
    key: string,
    title: string,
    subtitle: string,
    baseWeight: number,
    detailMeta: AssignmentDetailBase
  ) => {
    const sliderValue = weights[key] ?? baseWeight
    const saving = updateKey === key
    const sourceLabel = detailMeta.assignment_source
      ? (sourceLabels[detailMeta.assignment_source] ?? detailMeta.assignment_source)
      : null
    const isAuto = Boolean(detailMeta.auto_assigned)
    const isRequired = Boolean(detailMeta.required_for_competency)

    return (
      <div key={key} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-white shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            {sourceLabel && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700">
                {sourceLabel}
              </span>
            )}
            {isAuto && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700">
                <Sparkles className="h-3 w-3 mr-1" />
                Auto
              </span>
            )}
            {isRequired && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700">
                Pflicht
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={sliderValue}
            onChange={(e) => handleSliderChange(key, Number(e.target.value))}
            className="flex-1"
          />
          <span className="w-12 text-sm font-medium text-gray-700 text-right">{sliderValue.toFixed(2)}</span>
          <button
            onClick={() => handleSaveWeight(activityType, activityId, key, baseWeight)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern
          </button>
        </div>

        {detailMeta.notes && (
          <p className="text-xs text-gray-500">
            Notiz: {detailMeta.notes}
          </p>
        )}
      </div>
    )
  }

  const hasAnyData = detail && (
    detail.buildingBlock ||
    (detail.learningPlans && detail.learningPlans.length > 0) ||
    (detail.tasks && detail.tasks.length > 0)
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Kompetenz-Details</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{competencyName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span>Lade Details...</span>
            </div>
          ) : !hasAnyData ? (
            <div className="text-center py-12 text-gray-500">
              Keine Zuordnungen für diese Kompetenz gefunden.
            </div>
          ) : (
            <>
              <section>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Baustein-Ebene</h4>
                {detail?.buildingBlock ? (
                  renderAssignmentRow(
                    'building_block',
                    detail.buildingBlock.activity_id,
                    `building_block-${detail.buildingBlock.activity_id}`,
                    'Baustein',
                    'Direkte Zuordnung auf Baustein-Ebene',
                    clampWeight(detail.buildingBlock.weight),
                    detail.buildingBlock
                  )
                ) : (
                  <p className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-lg px-4 py-3">
                    Keine direkte Zuordnung auf Baustein-Ebene vorhanden.
                  </p>
                )}
              </section>

              <section>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Lernplan-Ebene</h4>
                {detail?.learningPlans && detail.learningPlans.length > 0 ? (
                  <div className="space-y-3">
                    {detail.learningPlans.map(lp =>
                      renderAssignmentRow(
                        'learning_plan',
                        lp.lpid,
                        `learning_plan-${lp.lpid}`,
                        lp.lernplan_name,
                        'Lernplan-Zuordnung',
                        clampWeight(lp.weight),
                        lp
                      )
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-lg px-4 py-3">
                    Keine Zuordnungen auf Lernplan-Ebene.
                  </p>
                )}
              </section>

              <section>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Aufgaben-Ebene</h4>
                {detail?.tasks && detail.tasks.length > 0 ? (
                  <div className="space-y-3">
                    {detail.tasks.map(task =>
                      renderAssignmentRow(
                        'task',
                        task.aid,
                        `task-${task.aid}`,
                        task.task_name || `Aufgabe ${task.aid}`,
                        `Lernplan: ${task.lernplan_name}`,
                        clampWeight(task.weight),
                        task
                      )
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-lg px-4 py-3">
                    Keine Aufgaben-Zuordnungen für diese Kompetenz gefunden.
                  </p>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function clampWeight(value: number | string | undefined): number {
  const numeric = Number(value ?? 0)
  if (Number.isNaN(numeric)) return 0
  return Math.min(1, Math.max(0, numeric))
}

export default function BuildingBlocks() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [allBausteine, setAllBausteine] = useState<Baustein[]>([])
  const [bausteine, setBausteine] = useState<Baustein[]>([])
  const [filteredBausteine, setFilteredBausteine] = useState<Baustein[]>([])
  const [folderTree, setFolderTree] = useState<BausteinFolder[]>([])
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null)
  const [currentFolder, setCurrentFolder] = useState<BausteinFolder | null>(null)
  const [currentSubfolders, setCurrentSubfolders] = useState<BausteinFolder[]>([])
  const [viewMode, setViewModeState] = useState<LrBausteinViewMode>(() => getBausteinViewMode())
  const [pinFilter, setPinFilter] = useState<'all' | 'pins'>('all')
  const [pinnedBausteine, setPinnedBausteine] = useState<Baustein[]>([])
  const [loading, setLoading] = useState(true)
  const [performanceStats, setPerformanceStats] = useState<PerformanceStatsData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<SortField>('Sortierung')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [currentUsername, setCurrentUsername] = useState<string>('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [recalculating, setRecalculating] = useState(false)
  
  // Panel States
  const [panelType, setPanelType] = useState<'A' | 'B' | 'C'>('A')
  const [selectedBaustein, setSelectedBaustein] = useState<Baustein | null>(null)
  const [editForm, setEditForm] = useState<Partial<Baustein>>({})
  const [klassen, setKlassen] = useState<Klasse[]>([])
  const [zuordnungen, setZuordnungen] = useState<Zuordnung[]>([])
  const [loadingKlassen, setLoadingKlassen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isLockedForOthers, setIsLockedForOthers] = useState(false)
  const [lernplaene, setLernplaene] = useState<Lernplan[]>([])
  const [loadingLernplaene, setLoadingLernplaene] = useState(false)
  const [selectedLernplanForEdit, setSelectedLernplanForEdit] = useState<LernplanDetail | null>(null)
  const [curricula, setCurricula] = useState<any[]>([])
  const [loadingCurricula, setLoadingCurricula] = useState(false)
  const [selectedCurriculumIds, setSelectedCurriculumIds] = useState<number[]>([])
  const [showCurriculumSelect, setShowCurriculumSelect] = useState(false)
  const [curriculumSearchTerm, setCurriculumSearchTerm] = useState('')
  const [curriculumCompetencies, setCurriculumCompetencies] = useState<Record<number, any[]>>({})
  const [loadingCompetencies, setLoadingCompetencies] = useState<Record<number, boolean>>({})
  const [savingCurricula, setSavingCurricula] = useState(false)
  const lastSavedCurriculumIdsRef = useRef<number[]>([])
  const saveCurriculaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sortierfunktion für Lernpläne
  const handleSortLernplaene = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Sortierte Lernpläne - immer nach Sortierung sortieren für Drag/Drop
  const sortedLernplaene = [...lernplaene].sort((a, b) => {
    // Für Drag/Drop immer nach Sortierung sortieren
    if (sortField === 'Sortierung' || !sortField) {
      return a.Sortierung - b.Sortierung
    }
    
    // Für andere Sortierungen
    let aValue: any = a[sortField as keyof Lernplan]
    let bValue: any = b[sortField as keyof Lernplan]

    // Spezielle Behandlung für Datum
    if (sortField === 'START_DATUM' || sortField === 'END_DATUM') {
      aValue = aValue ? new Date(aValue) : new Date(0)
      bValue = bValue ? new Date(bValue) : new Date(0)
    }

    // Spezielle Behandlung für Zahlen
    const numericFields = ['GESAMT_ZEITBEDARF', 'AUFGABEN_ANZAHL', 'Sortierung', 'COMPETENCY_COUNT'] as const
    if (numericFields.includes(sortField as any)) {
      aValue = Number(aValue) || 0
      bValue = Number(bValue) || 0
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  })
  const [editLernplanForm, setEditLernplanForm] = useState<Partial<LernplanDetail>>({})
  const [savingLernplan, setSavingLernplan] = useState(false)
  const [tasks, setTasks] = useState<Task[]>([])
  const [loadingTasks, setLoadingTasks] = useState(false)
  const [updatingTaskOrder, setUpdatingTaskOrder] = useState(false)
  const [creatingTask, setCreatingTask] = useState(false)
  const [deletingTask, setDeletingTask] = useState<number | null>(null)
  const [showTaskCompetencyModal, setShowTaskCompetencyModal] = useState(false)
  const [selectedTaskForCompetency, setSelectedTaskForCompetency] = useState<number | null>(null)
  const [competencyRefreshKey, setCompetencyRefreshKey] = useState(0)
  const [showCompetencyDetailModal, setShowCompetencyDetailModal] = useState(false)
  const [competencyDetailLoading, setCompetencyDetailLoading] = useState(false)
  const [competencyDetailData, setCompetencyDetailData] = useState<CompetencyOriginDetailData | null>(null)
  const [selectedCompetencyMeta, setSelectedCompetencyMeta] = useState<{ id: number; name: string } | null>(null)
  const [weightUpdateKey, setWeightUpdateKey] = useState<string | null>(null)
  const [topDownEnabled, setTopDownEnabled] = useState(false)
  const [topDownSyncing, setTopDownSyncing] = useState(false)
  const [learningPlanTopDownEnabled, setLearningPlanTopDownEnabled] = useState(false)
  const [learningPlanTopDownSyncing, setLearningPlanTopDownSyncing] = useState(false)
  const [removingAllCompetencies, setRemovingAllCompetencies] = useState(false)
  const [removingLernplanCompetencies, setRemovingLernplanCompetencies] = useState(false)

  const selectedBausteinId = selectedBaustein?.BID

  const handleBuildingBlockCompetencyUpdate = useCallback((assignments?: CompetencyAssignment[]) => {
    if (!selectedBausteinId) return
    const competencyCount = assignments ? new Set(assignments.map(a => a.competency_id)).size : 0

    setSelectedBaustein(prev =>
      prev && prev.BID === selectedBausteinId ? { ...prev, COMPETENCY_COUNT: competencyCount } : prev
    )

    setBausteine(prev =>
      prev.map(b =>
        b.BID === selectedBausteinId ? { ...b, COMPETENCY_COUNT: competencyCount } : b
      )
    )
  }, [selectedBausteinId])

  const fetchCompetencyDetails = useCallback(async (competencyId: number, showLoading = false) => {
    if (!selectedBausteinId) return null
    try {
      if (showLoading) {
        setCompetencyDetailLoading(true)
      }
      const response = await apiClient.get(`/competency-assignments/building_block/${selectedBausteinId}/${competencyId}/details`)
      if (response.data.success) {
        setCompetencyDetailData(response.data.data)
        return response.data.data as CompetencyOriginDetailData
      }
      throw new Error(response.data.error || response.data.message || 'Unbekannter Fehler')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Laden der Kompetenz-Details: ${message}`)
      setCompetencyDetailData(null)
      return null
    } finally {
      if (showLoading) {
        setCompetencyDetailLoading(false)
      }
    }
  }, [selectedBausteinId])

  const handleShowCompetencyDetails = useCallback(async (assignment: CompetencyAssignment) => {
    if (!selectedBausteinId) return
    setSelectedCompetencyMeta({ id: assignment.competency_id, name: assignment.competency_name })
    setCompetencyDetailData(null)
    setShowCompetencyDetailModal(true)
    const detail = await fetchCompetencyDetails(assignment.competency_id, true)
    if (!detail) {
      setShowCompetencyDetailModal(false)
      setSelectedCompetencyMeta(null)
    }
  }, [selectedBausteinId, fetchCompetencyDetails])

  const handleUpdateAssignmentWeight = useCallback(async (activityType: ActivityType, activityId: number, newWeight: number) => {
    if (!selectedCompetencyMeta) return
    const clampedWeight = clampWeight(newWeight)
    try {
      setWeightUpdateKey(`${activityType}-${activityId}`)
      const response = await apiClient.patch(`/competency-assignments/${activityType}/${activityId}/${selectedCompetencyMeta.id}`, {
        weight: clampedWeight
      })
      if (!response.data.success) {
        throw new Error(response.data.error || response.data.message || 'Unbekannter Fehler')
      }
      toast.success('Gewichtung aktualisiert')
      await fetchCompetencyDetails(selectedCompetencyMeta.id)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Aktualisieren der Gewichtung: ${message}`)
    } finally {
      setWeightUpdateKey(null)
    }
  }, [selectedCompetencyMeta, fetchCompetencyDetails])

  const handleTopDownSync = useCallback(async () => {
    if (!selectedBausteinId) {
      toast.error('Bitte wählen Sie zuerst einen Baustein aus')
      return
    }

    try {
      setTopDownSyncing(true)
      const response = await apiClient.post(
        `/competency-assignments/building_block/${selectedBausteinId}/top-down-sync`,
        {
          includeLearningPlans: true,
          includeTasks: true
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.error || response.data.message || 'Unbekannter Fehler')
      }

      const stats = response.data.data || {}
      toast.success(
        `Top-Down abgeschlossen: ${stats.learningPlansUpdated || 0} Lernpläne & ${stats.tasksUpdated || 0} Aufgaben aktualisiert`
      )
      setCompetencyRefreshKey(prev => prev + 1)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Top-Down fehlgeschlagen: ${message}`)
    } finally {
      setTopDownSyncing(false)
      setTopDownEnabled(false)
    }
  }, [selectedBausteinId])

  const handleLearningPlanTopDownSync = useCallback(async () => {
    const lpid = selectedLernplanForEdit?.LPID
    if (!lpid) {
      toast.error('Bitte wählen Sie zuerst einen Lernplan aus')
      return
    }

    try {
      setLearningPlanTopDownSyncing(true)
      const response = await apiClient.post(
        `/competency-assignments/learning_plan/${Number(lpid)}/top-down-sync`,
        {
          includeTasks: true
        }
      )

      if (!response.data.success) {
        throw new Error(response.data.error || response.data.message || 'Unbekannter Fehler')
      }

      const stats = response.data.data || {}
      toast.success(
        `Top-Down abgeschlossen: ${stats.tasksUpdated || 0} Aufgaben aktualisiert`
      )
      setCompetencyRefreshKey(prev => prev + 1)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Top-Down fehlgeschlagen: ${message}`)
    } finally {
      setLearningPlanTopDownSyncing(false)
      setLearningPlanTopDownEnabled(false)
    }
  }, [selectedLernplanForEdit?.LPID])

  // Handler für rekursives Entfernen aller Kompetenzen vom Baustein
  const handleRemoveAllCompetenciesFromBuildingBlock = useCallback(async () => {
    if (!selectedBausteinId) {
      toast.error('Bitte wählen Sie zuerst einen Baustein aus')
      return
    }

    const confirmed = window.confirm(
      'Möchten Sie wirklich ALLE Kompetenz-Zuordnungen von diesem Baustein entfernen?\n\n' +
      'Dies betrifft:\n' +
      '• Alle Kompetenzen des Bausteins selbst\n' +
      '• Alle Kompetenzen aller Lernpläne\n' +
      '• Alle Kompetenzen aller Aufgaben\n\n' +
      'Diese Aktion kann nicht rückgängig gemacht werden!'
    )

    if (!confirmed) return

    try {
      setRemovingAllCompetencies(true)
      const response = await apiClient.delete(
        `/competency-assignments/building_block/${selectedBausteinId}/remove-all-recursive`
      )

      if (!response.data.success) {
        throw new Error(response.data.error || response.data.message || 'Unbekannter Fehler')
      }

      const stats = response.data.data || {}
      const totalRemoved = (stats.buildingBlockAssignmentsRemoved || 0) + 
                          (stats.learningPlanAssignmentsRemoved || 0) + 
                          (stats.taskAssignmentsRemoved || 0)

      toast.success(
        `${totalRemoved} Kompetenz-Zuordnungen entfernt:\n` +
        `• Baustein: ${stats.buildingBlockAssignmentsRemoved || 0}\n` +
        `• Lernpläne: ${stats.learningPlanAssignmentsRemoved || 0}\n` +
        `• Aufgaben: ${stats.taskAssignmentsRemoved || 0}`,
        { duration: 5000 }
      )
      
      // Update UI
      handleBuildingBlockCompetencyUpdate([])
      setCompetencyRefreshKey(prev => prev + 1)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Entfernen der Kompetenzen: ${message}`)
    } finally {
      setRemovingAllCompetencies(false)
    }
  }, [selectedBausteinId, handleBuildingBlockCompetencyUpdate])

  // Handler für rekursives Entfernen aller Kompetenzen vom Lernplan
  const handleRemoveAllCompetenciesFromLearningPlan = useCallback(async () => {
    if (!selectedLernplanForEdit?.LPID) {
      toast.error('Bitte wählen Sie zuerst einen Lernplan aus')
      return
    }

    const confirmed = window.confirm(
      'Möchten Sie wirklich ALLE Kompetenz-Zuordnungen von diesem Lernplan entfernen?\n\n' +
      'Dies betrifft:\n' +
      '• Alle Kompetenzen des Lernplans selbst\n' +
      '• Alle Kompetenzen aller Aufgaben dieses Lernplans\n\n' +
      'Diese Aktion kann nicht rückgängig gemacht werden!'
    )

    if (!confirmed) return

    try {
      setRemovingLernplanCompetencies(true)
      const response = await apiClient.delete(
        `/competency-assignments/learning_plan/${selectedLernplanForEdit.LPID}/remove-all-recursive`
      )

      if (!response.data.success) {
        throw new Error(response.data.error || response.data.message || 'Unbekannter Fehler')
      }

      const stats = response.data.data || {}
      const totalRemoved = (stats.learningPlanAssignmentsRemoved || 0) + 
                          (stats.taskAssignmentsRemoved || 0)

      toast.success(
        `${totalRemoved} Kompetenz-Zuordnungen entfernt:\n` +
        `• Lernplan: ${stats.learningPlanAssignmentsRemoved || 0}\n` +
        `• Aufgaben: ${stats.taskAssignmentsRemoved || 0}`,
        { duration: 5000 }
      )
      
      // Update UI
      setCompetencyRefreshKey(prev => prev + 1)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Entfernen der Kompetenzen: ${message}`)
    } finally {
      setRemovingLernplanCompetencies(false)
    }
  }, [selectedLernplanForEdit?.LPID])

  // Drag & Drop Sensoren
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    return () => {
      if (saveCurriculaTimeoutRef.current) {
        clearTimeout(saveCurriculaTimeoutRef.current)
        saveCurriculaTimeoutRef.current = null
      }
    }
  }, [])

const saveCurriculaSelection = useCallback(async (bausteinId: number, curriculumIds: number[]) => {
  try {
    setSavingCurricula(true)
    const response = await apiClient.put(`/building-blocks/${bausteinId}/curricula`, { curriculumIds })
    if (!response.data.success) {
      throw new Error(response.data.error || response.data.message || 'Failed to update curricula')
    }
    const saved = normalizeCurriculumIds(response.data.data?.curriculumIds || curriculumIds)
    lastSavedCurriculumIdsRef.current = saved
    setBausteine(prev =>
      prev.map(b =>
        b.BID === bausteinId ? { ...b, curriculumIds: saved } : b
      )
    )
    // Aktualisiere selectedBaustein nur, wenn es der aktuelle Baustein ist
    // Aber setze lastSavedCurriculumIdsRef zuerst, damit der useEffect nicht mit skipSave überschreibt
    setSelectedBaustein(prev => {
      if (prev && prev.BID === bausteinId) {
        // Prüfe ob sich die curriculumIds tatsächlich geändert haben
        const prevIds = normalizeCurriculumIds(prev.curriculumIds || []).sort((a, b) => a - b).join(',')
        const newIds = saved.sort((a, b) => a - b).join(',')
        if (prevIds !== newIds) {
          console.log('✅ Aktualisiere selectedBaustein mit gespeicherten Curricula', { prevIds, newIds })
          return { ...prev, curriculumIds: saved }
        }
      }
      return prev
    })
    toast.success('Curricula erfolgreich gespeichert', {
      icon: '✅',
      duration: 2000
    })
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unbekannter Fehler'
    toast.error(`Fehler beim Speichern der Curricula: ${message}`)
  } finally {
    setSavingCurricula(false)
  }
}, [])

const fetchCurriculumCompetencies = useCallback(async (curriculumId: number) => {
  // Check if already loaded or loading
  setLoadingCompetencies(prev => {
    if (prev[curriculumId]) {
      return prev // Already loading
    }
    return { ...prev, [curriculumId]: true }
  })
  
  try {
    const response = await apiClient.get(`/curricula/${curriculumId}/competencies`)
    if (response.data.success) {
      setCurriculumCompetencies(prev => {
        if (prev[curriculumId]) {
          return prev // Already set
        }
        return {
          ...prev,
          [curriculumId]: response.data.data || []
        }
      })
    }
  } catch (error) {
    console.error(`Error fetching competencies for curriculum ${curriculumId}:`, error)
  } finally {
    setLoadingCompetencies(prev => ({ ...prev, [curriculumId]: false }))
  }
}, [])

const updateCurriculumSelection = useCallback(
  (updater: number[] | ((prev: number[]) => number[]), options: { skipSave?: boolean } = {}) => {
    const { skipSave = false } = options
    setSelectedCurriculumIds(prev => {
      const resolved = typeof updater === 'function' ? updater(prev) : updater
      const normalized = normalizeCurriculumIds(resolved)
      console.log('📝 updateCurriculumSelection aufgerufen', { 
        prev, 
        resolved, 
        normalized, 
        skipSave, 
        selectedBaustein: selectedBaustein?.BID,
        lastSaved: lastSavedCurriculumIdsRef.current,
        arraysEqual: arraysEqual(normalized, prev),
        arraysEqualLastSaved: arraysEqual(normalized, lastSavedCurriculumIdsRef.current)
      })
      
      if (arraysEqual(normalized, prev)) {
        console.log('⏭️  Arrays sind gleich (normalized === prev), überspringe Update')
        return prev
      }
      
      if (!skipSave && selectedBaustein) {
        if (
          !arraysEqual(normalized, lastSavedCurriculumIdsRef.current)
        ) {
          if (saveCurriculaTimeoutRef.current) {
            clearTimeout(saveCurriculaTimeoutRef.current)
          }
          const idsForSave = [...normalized]
          const bausteinId = selectedBaustein.BID
          console.log('🔄 Curriculum-Auswahl geändert, speichere in 600ms...', { bausteinId, idsForSave, prev: lastSavedCurriculumIdsRef.current })
          saveCurriculaTimeoutRef.current = setTimeout(() => {
            console.log('💾 Speichere Curriculum-Auswahl...', { bausteinId, idsForSave })
            saveCurriculaSelection(bausteinId, idsForSave)
            saveCurriculaTimeoutRef.current = null
          }, 600)
        } else {
          console.log('⏭️  Keine Änderung zu lastSaved, überspringe Speicherung', { normalized, lastSaved: lastSavedCurriculumIdsRef.current })
        }
      } else {
        if (!selectedBaustein) {
          console.warn('⚠️  selectedBaustein ist nicht gesetzt, kann nicht speichern', { skipSave, selectedBaustein })
        }
        if (skipSave) {
          console.log('⏭️  skipSave=true, überspringe Speicherung')
        }
      }
      return normalized
    })
  },
  [selectedBaustein, saveCurriculaSelection]
)

useEffect(() => {
  setShowCompetencyDetailModal(false)
  setCompetencyDetailData(null)
  setSelectedCompetencyMeta(null)
  setWeightUpdateKey(null)
  if (saveCurriculaTimeoutRef.current) {
    clearTimeout(saveCurriculaTimeoutRef.current)
    saveCurriculaTimeoutRef.current = null
  }

  if (selectedBaustein) {
    const sanitized = normalizeCurriculumIds(selectedBaustein.curriculumIds || [])
    // Nur setzen, wenn sich die curriculumIds tatsächlich geändert haben
    const currentIds = selectedCurriculumIds.sort((a, b) => a - b).join(',')
    const newIds = sanitized.sort((a, b) => a - b).join(',')
    const lastSavedIds = lastSavedCurriculumIdsRef.current.sort((a, b) => a - b).join(',')
    
    // Prüfe ob sich der Baustein geändert hat (nicht nur die Curricula)
    // Wenn lastSaved bereits mit sanitized übereinstimmt, wurde gerade gespeichert - überspringe
    if (lastSavedIds === newIds && currentIds === newIds) {
      console.log('⏭️  Curricula bereits korrekt gesetzt und gespeichert, überspringe Update', { currentIds, newIds, lastSavedIds })
      return // Keine Änderung nötig
    }
    
    if (currentIds !== newIds) {
      console.log('🔄 Baustein geändert, setze Curricula neu', { currentIds, newIds, selectedBausteinId, lastSavedIds })
    lastSavedCurriculumIdsRef.current = sanitized
    updateCurriculumSelection(sanitized, { skipSave: true })
  } else {
      console.log('⏭️  Curricula bereits korrekt gesetzt, überspringe Update', { currentIds, newIds })
      // Aktualisiere nur lastSaved, wenn nötig
      if (!arraysEqual(sanitized, lastSavedCurriculumIdsRef.current)) {
        lastSavedCurriculumIdsRef.current = sanitized
      }
    }
  } else {
    if (selectedCurriculumIds.length > 0) {
      console.log('🔄 Kein Baustein ausgewählt, leere Curricula', { selectedBausteinId })
    lastSavedCurriculumIdsRef.current = []
    updateCurriculumSelection([], { skipSave: true })
  }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedBausteinId, selectedBaustein?.BID, selectedBaustein?.curriculumIds?.join(',')])

// Separate effect to load competencies for selected curricula
useEffect(() => {
  if (!selectedBaustein) return
  
  const sanitized = normalizeCurriculumIds(selectedBaustein.curriculumIds || [])
  const curriculumIdsString = sanitized.sort((a, b) => a - b).join(',')
  
  sanitized.forEach((curriculumId) => {
    setCurriculumCompetencies(prev => {
      if (!prev[curriculumId]) {
        // Load asynchronously to avoid blocking render
        setTimeout(() => fetchCurriculumCompetencies(curriculumId), 0)
      }
      return prev
    })
  })
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedBaustein?.BID, selectedBaustein?.curriculumIds?.join(',')])

  // Fetch data from backend
  useEffect(() => {
    fetchBausteine()
    fetchFolderTree()
    fetchCurrentUser()
    fetchCurricula()
    fetchPerformanceStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (pinFilter === 'pins') return
    fetchFolderContents(selectedFolderId).catch((error) => {
      console.error('Error fetching folder contents:', error)
      toast.error('Ordnerinhalt konnte nicht geladen werden')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderId, pinFilter])

  useEffect(() => {
    if (pinFilter !== 'pins') return
    fetchPinnedBausteine().catch((error) => {
      console.error('Error fetching pinned building blocks:', error)
      toast.error('Favoriten konnten nicht geladen werden')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinFilter])

  const deepLinkLpidHandledRef = useRef<string | null>(null)

  // Deep link from Lernbüro legend: /building-blocks?lpid=…
  useEffect(() => {
    const lpidParam = searchParams.get('lpid')?.trim()
    if (!lpidParam || loading || error) return
    if (deepLinkLpidHandledRef.current === lpidParam) return

    deepLinkLpidHandledRef.current = lpidParam

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('lpid')
    setSearchParams(nextParams, { replace: true })

    void (async () => {
      const detail = await handleEditLernplan(lpidParam)
      if (!detail?.BID) return

      const baustein = allBausteine.find((b) => b.BID === detail.BID)
      if (!baustein) return

      setSelectedBaustein(baustein)
      setEditForm({
        NAME: baustein.NAME,
        FACH: baustein.FACH,
        STUFE: baustein.STUFE,
        JAHR: baustein.JAHR,
        START_DATUM: baustein.START_DATUM,
        END_DATUM: baustein.END_DATUM,
        INFO: baustein.INFO,
        Eigentuemer: baustein.Eigentuemer
      })
      setIsLockedForOthers(baustein.Eigentuemer !== 'alle')
      await fetchKlassen()
      await fetchZuordnungen(baustein.BID)
      await fetchLernplaene(baustein.BID)
    })()
  }, [loading, error, searchParams, allBausteine, setSearchParams])

  // Filter bausteine when search term changes
  useEffect(() => {
    const source = pinFilter === 'pins' ? pinnedBausteine : bausteine
    if (!searchTerm) {
      setFilteredBausteine(source)
    } else {
      const sourceForSearch = pinFilter === 'pins' ? pinnedBausteine : allBausteine
      const filtered = sourceForSearch.filter(baustein =>
        (baustein.NAME || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (baustein.FACH || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (baustein.STUFE || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (baustein.JAHR || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        (baustein.INFO || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredBausteine(filtered)
    }
  }, [searchTerm, bausteine, allBausteine, pinFilter, pinnedBausteine])

  // Sortierte Bausteine für die Tabelle in Panel A
  const sortedBausteine = useMemo(() => {
    const data = [...filteredBausteine]
    if (sortField === 'Sortierung') return data

    return data.sort((a, b) => {
      let aValue: any
      let bValue: any

      if (sortField === 'PERF_STUDENT_COUNT') {
        const aPerf = performanceStats?.bausteine.find(p => p.BID === a.BID)
        const bPerf = performanceStats?.bausteine.find(p => p.BID === b.BID)
        aValue = aPerf?.student_count ?? -1
        bValue = bPerf?.student_count ?? -1
      } else if (sortField === 'PERF_AVG_PCT') {
        const aPerf = performanceStats?.bausteine.find(p => p.BID === a.BID)
        const bPerf = performanceStats?.bausteine.find(p => p.BID === b.BID)
        aValue = aPerf ? Number(aPerf.avg_completion_pct) : -1
        bValue = bPerf ? Number(bPerf.avg_completion_pct) : -1
      } else if (sortField === 'START_DATUM') {
        aValue = getBausteinZeitfensterStart(a) ? new Date(getBausteinZeitfensterStart(a)) : new Date(0)
        bValue = getBausteinZeitfensterStart(b) ? new Date(getBausteinZeitfensterStart(b)) : new Date(0)
      } else if (sortField === 'END_DATUM') {
        aValue = getBausteinZeitfensterEnd(a) ? new Date(getBausteinZeitfensterEnd(a)) : new Date(0)
        bValue = getBausteinZeitfensterEnd(b) ? new Date(getBausteinZeitfensterEnd(b)) : new Date(0)
      } else {
        aValue = (a as any)[sortField as keyof Baustein]
        bValue = (b as any)[sortField as keyof Baustein]
        aValue = typeof aValue === 'string' ? aValue.toLowerCase() : aValue
        bValue = typeof bValue === 'string' ? bValue.toLowerCase() : bValue
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredBausteine, sortField, sortDirection, performanceStats])

  const breadcrumbItems = useMemo(
    () => getFolderBreadcrumb(folderTree, selectedFolderId),
    [folderTree, selectedFolderId]
  )

  // Helper function to normalize ISO date strings to YYYY-MM-DD format
  const normalizeDateString = (dateStr: string | null | undefined): string => {
    if (!dateStr) return ''
    // If it's an ISO string (e.g., "2025-12-07T23:00:00.000Z"), extract just the date part
    if (typeof dateStr === 'string' && dateStr.includes('T')) {
      return dateStr.split('T')[0]
    }
    return dateStr
  }

  const normalizeBaustein = useCallback((item: any): Baustein => ({
    ...item,
    START_DATUM: normalizeDateString(item.START_DATUM),
    END_DATUM: normalizeDateString(item.END_DATUM),
    ZEITFENSTER_START: normalizeDateString(item.ZEITFENSTER_START),
    ZEITFENSTER_END: normalizeDateString(item.ZEITFENSTER_END),
    HAT_AKTIVE_LERNPLAENE: Number(item.HAT_AKTIVE_LERNPLAENE) === 1,
    folder_id: item.folder_id ? Number(item.folder_id) : null,
    curriculumIds: item.curriculumIds || [],
    pinned: Boolean(item.pinned)
  }), [])

  const fetchBausteine = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/building-blocks')
      if (response.data.success) {
        const bausteineWithCurricula = response.data.data.map((item: any) => normalizeBaustein(item))
        setAllBausteine(bausteineWithCurricula)
      } else {
        throw new Error(response.data.error || 'Failed to fetch bausteine')
      }
    } catch (error) {
      console.error('Error fetching bausteine:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Laden der Bausteine: ${errorMessage}`)
      setError('Fehler beim Laden der Bausteine')
    } finally {
      setLoading(false)
    }
  }

  async function fetchFolderTree() {
    const response = await apiClient.get('/building-blocks/folders/tree')
    if (response.data?.success) {
      setFolderTree(response.data.data || [])
    }
  }

  async function fetchPinnedBausteine() {
    const response = await apiClient.get('/building-blocks/pins')
    if (response.data?.success) {
      setPinnedBausteine((response.data.data || []).map((item: any) => normalizeBaustein({ ...item, pinned: true })))
    }
  }

  async function fetchFolderContents(folderId: number | null) {
    const endpoint =
      folderId == null
        ? '/building-blocks/folders/root/contents'
        : `/building-blocks/folders/${folderId}/contents`
    const response = await apiClient.get(endpoint)
    if (response.data?.success) {
      const payload = response.data.data || {}
      setCurrentFolder(payload.folder || null)
      setCurrentSubfolders(payload.subfolders || [])
      setBausteine((payload.bausteine || []).map((item: any) => normalizeBaustein(item)))
    }
  }

  async function moveBausteinToFolder(bid: number, folderId: number | null) {
    await apiClient.put(`/building-blocks/${bid}/move`, { folderId })
    if (pinFilter !== 'pins' && folderId !== selectedFolderId) {
      // Optimistisch aus der aktuellen Ordneransicht entfernen.
      setBausteine((prev) => prev.filter((item) => item.BID !== bid))
    }

    const refreshed = await apiClient.get('/building-blocks')
    if (refreshed.data?.success) {
      setAllBausteine(refreshed.data.data.map((item: any) => normalizeBaustein(item)))
    }

    // Nach Verschieben immer alles Relevante synchronisieren, damit kein manueller Reload nötig ist.
    await Promise.all([
      fetchFolderTree(),
      fetchFolderContents(selectedFolderId),
      fetchPinnedBausteine()
    ])
  }

  const fetchPerformanceStats = async () => {
    try {
      const response = await apiClient.get('/building-blocks/performance-stats')
      if (response.data.success) {
        setPerformanceStats(response.data.data)
      }
    } catch (error) {
      // Performance stats are optional — silently ignore errors
      console.warn('Could not load performance stats:', error)
    }
  }

  const handleRecalculateStats = async () => {
    if (recalculating) return
    try {
      setRecalculating(true)
      const response = await apiClient.post('/building-blocks/performance-stats/recalculate', {})
      if (response.data.success) {
        toast.success('Berechnung gestartet. Statistiken werden im Hintergrund aktualisiert.')
        // Reload stats after a short delay to pick up any fast completions
        setTimeout(() => {
          fetchPerformanceStats()
          setRecalculating(false)
        }, 3000)
      } else {
        toast.error(response.data.error || 'Fehler beim Starten der Berechnung')
        setRecalculating(false)
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Unbekannter Fehler'
      toast.error(`Fehler: ${msg}`)
      setRecalculating(false)
    }
  }

  const fetchCurrentUser = async () => {
    try {
      const response = await apiClient.get('/auth/me')
      const userData = response.data?.data || response.data
      setCurrentUsername(userData.username || '')
      const role = (userData.role || '').toLowerCase()
      setIsAdmin(role === 'admin')
    } catch (error) {
      console.error('Error fetching current user:', error)
    }
  }

  const fetchKlassen = async () => {
    try {
      setLoadingKlassen(true)
      const response = await apiClient.get('/students/classes')
      if (response.data?.success) {
        setKlassen(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching classes:', error)
    } finally {
      setLoadingKlassen(false)
    }
  }

  const fetchCurricula = async () => {
    try {
      setLoadingCurricula(true)
      const response = await apiClient.get('/curricula')
      if (response.data.success) {
        setCurricula(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching curricula:', error)
    } finally {
      setLoadingCurricula(false)
    }
  }


  const fetchZuordnungen = async (bid: number) => {
    try {
      const response = await apiClient.get(`/building-blocks/${bid}/classes`)
      if (response.data?.success) {
        const zuordnungen = (response.data.data || []).map((klasse: string) => ({
          BID: bid.toString(),
          Klasse: klasse
        }))
        setZuordnungen(zuordnungen)
      }
    } catch (error) {
      console.error('Error fetching zuordnungen:', error)
    }
  }

  const fetchLernplaene = async (bid: number) => {
    try {
      setLoadingLernplaene(true)
      const response = await apiClient.get('/learning-plans', {
        params: { bid, limit: 500, offset: 0 }
      })

      if (response.data?.success) {
        const rows = Array.isArray(response.data.data) ? response.data.data : []
        const normalizedLernplaene = rows.map((lp: any) => ({
          ...lp,
          LPID: String(lp.LPID),
          Sortierung: Number(lp.Sortierung) || 0,
          START_DATUM: normalizeDateString(lp.START_DATUM),
          END_DATUM: normalizeDateString(lp.END_DATUM)
        }))
        setLernplaene(normalizedLernplaene)
      } else {
        setLernplaene([])
      }
    } catch (error) {
      console.error('Error fetching lernplaene:', error)
      toast.error('Lernpläne konnten nicht geladen werden.')
      setLernplaene([])
    } finally {
      setLoadingLernplaene(false)
    }
  }

  const fetchTasks = async (lpid: number) => {
    try {
      setLoadingTasks(true)
      const response = await apiClient.get('/tasks', { params: { lpid } })
      if (response.data?.success) {
        setTasks(response.data.data || [])
      } else {
        throw new Error(response.data?.error || 'Failed to fetch tasks')
      }
    } catch (error) {
      console.error('Error fetching tasks:', error)
      toast.error(`Fehler beim Laden der Aufgaben: ${getAxiosErrorMessage(error, 'Unbekannter Fehler')}`)
    } finally {
      setLoadingTasks(false)
    }
  }

  const updateTaskField = async (taskId: number, field: string, value: any) => {
    const updateData: any = { [field]: value }
    
    // Remove null/undefined values
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === null || updateData[key] === undefined) {
        delete updateData[key]
      }
    })
    
    const response = await apiClient.put(`/tasks/${taskId}`, updateData)
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Update failed')
    }
    const data = response.data
    
    // Update local state
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.AID === taskId 
          ? { ...task, [field]: value }
          : task
      )
    )
    
    return data
  }

  const createNewTask = async () => {
    if (!selectedLernplanForEdit) return

    try {
      setCreatingTask(true)
      
      const newTaskData = {
        LPID: selectedLernplanForEdit.LPID,
        Kurzberzeichnung: 'Neue Aufgabe',
        Aufgabenstellung: 'Aufgabenstellung eingeben...',
        Sortierung: tasks.length + 1,
        SForm: 'EA',
        HMittel: '-',
        Einzelarbeit: false,
        Partnerarbeit: false,
        Gruppenarbeit: false,
        PCTABLET: false,
        Zusatzaufgabe: 0,
        Schwer: false
      }

      const response = await apiClient.post('/tasks', newTaskData)
      const data = response.data
      if (!data.success) {
        throw new Error(data.message || data.error || 'Create failed')
      }

      // Add new task to local state
      setTasks(prevTasks => [...prevTasks, data.data])
      toast.success('Neue Aufgabe erfolgreich erstellt')

    } catch (error) {
      console.error('Error creating task:', error)
      toast.error(`Fehler beim Erstellen der Aufgabe: ${getAxiosErrorMessage(error, 'Unbekannter Fehler')}`)
    } finally {
      setCreatingTask(false)
    }
  }

  const deleteTask = async (aid: number) => {
    if (!confirm('Möchten Sie diese Aufgabe wirklich löschen?')) return

    try {
      setDeletingTask(aid)
      
      const response = await apiClient.delete(`/tasks/${aid}`)
      const data = response.data
      if (!data.success) {
        throw new Error(data.message || data.error || 'Delete failed')
      }

      // Remove task from local state
      setTasks(prevTasks => prevTasks.filter(task => task.AID !== aid))
      toast.success('Aufgabe erfolgreich gelöscht')

    } catch (error) {
      console.error('Error deleting task:', error)
      toast.error(`Fehler beim Löschen der Aufgabe: ${getAxiosErrorMessage(error, 'Unbekannter Fehler')}`)
    } finally {
      setDeletingTask(null)
    }
  }

  const handleShowTaskCompetencies = (taskId: number) => {
    setSelectedTaskForCompetency(taskId)
    setShowTaskCompetencyModal(true)
  }

  const updateTaskOrder = async (newTasks: Task[]) => {
    try {
      setUpdatingTaskOrder(true)
      
      // Update Sortierung for each task based on new order
      // Sortierung wird in Aufgaben.Sortierung gespeichert where Aufgaben.LPID = Lernplaene.LPID
      const updatePromises = newTasks.map(async (task, index) => {
        // Filter out null/undefined values to avoid validation errors
        const updateData: any = {
          ...task,
          Sortierung: index + 1  // Neue Sortierung basierend auf der Reihenfolge im Array
        }
        
        // Remove null/undefined values, but keep Sortierung even if it's 0
        Object.keys(updateData).forEach(key => {
          if (updateData[key] === null || updateData[key] === undefined) {
            delete updateData[key]
          }
        })
        
        // Ensure Sortierung is always included as a number
        updateData.Sortierung = index + 1
        
        const response = await apiClient.put(`/tasks/${task.AID}`, updateData)
        if (!response.data?.success) {
          throw new Error(response.data?.message || response.data?.error || 'Update failed')
        }
        return response.data
      })
      
      await Promise.all(updatePromises)
      
      // All updates successful
      setTasks(newTasks)
      toast.success('Aufgabenreihenfolge erfolgreich aktualisiert')
    } catch (error) {
      console.error('Error updating task order:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler'
      console.error('Full error details:', error)
      toast.error(`Fehler beim Aktualisieren der Aufgabenreihenfolge: ${errorMessage}`)
    } finally {
      setUpdatingTaskOrder(false)
    }
  }

  const updateLernplanOrder = async (newLernplaene: Lernplan[]) => {
    try {
      setUpdatingTaskOrder(true)
      
      // Update Sortierung for each lernplan based on new order
      const updatePromises = newLernplaene.map(async (lernplan, index) => {
        const response = await apiClient.put(`/learning-plans/${lernplan.LPID}/sortierung`, {
          sortierung: index + 1
        })
        if (!response.data?.success) {
          throw new Error(response.data?.message || response.data?.error || 'Update failed')
        }
        return response.data
      })
      
      await Promise.all(updatePromises)
      
      // All updates successful - update local state with new sortierung values
      const updatedLernplaene = newLernplaene.map((lernplan, index) => ({
        ...lernplan,
        Sortierung: index + 1
      }))
      setLernplaene(updatedLernplaene)
      toast.success('Lernplanreihenfolge erfolgreich aktualisiert')
    } catch (error) {
      console.error('Error updating lernplan order:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Aktualisieren der Lernplanreihenfolge: ${errorMessage}`)
      
      // Reload the lernplaene to get the correct order from server
      if (selectedBaustein) {
        fetchLernplaene(selectedBaustein.BID)
      }
    } finally {
      setUpdatingTaskOrder(false)
    }
  }

  const handleRowClick = async (baustein: Baustein) => {
    setSelectedBaustein(baustein)
    setEditForm({
      NAME: baustein.NAME,
      FACH: baustein.FACH,
      STUFE: baustein.STUFE,
      JAHR: baustein.JAHR,
      START_DATUM: baustein.START_DATUM,
      END_DATUM: baustein.END_DATUM,
      INFO: baustein.INFO,
      Eigentuemer: baustein.Eigentuemer
    })
    
    // Reset Curriculum-Auswahl beim Baustein-Wechsel und mit vorhandenen Zuordnungen füllen
    const sanitizedCurricula = normalizeCurriculumIds(baustein.curriculumIds || [])
    lastSavedCurriculumIdsRef.current = sanitizedCurricula
    updateCurriculumSelection(sanitizedCurricula, { skipSave: true })
    
    // Setze den Checkbox-Status basierend auf dem aktuellen Eigentümer
    setIsLockedForOthers(baustein.Eigentuemer !== 'alle')
    await fetchKlassen()
    await fetchZuordnungen(baustein.BID)
    await fetchLernplaene(baustein.BID)
    setPanelType('B')
  }

  const handleBackToPanelA = () => {
    setPanelType('A')
    setSelectedBaustein(null)
    setEditForm({})
    setZuordnungen([])
    setLernplaene([])
    updateCurriculumSelection([], { skipSave: true })
    lastSavedCurriculumIdsRef.current = []
    if (saveCurriculaTimeoutRef.current) {
      clearTimeout(saveCurriculaTimeoutRef.current)
      saveCurriculaTimeoutRef.current = null
    }
  }


  const applyBausteinUpdateToState = (updated: Baustein) => {
    const normalized: Baustein = {
      ...updated,
      START_DATUM: normalizeDateString(updated.START_DATUM),
      END_DATUM: normalizeDateString(updated.END_DATUM),
      ZEITFENSTER_START: normalizeDateString(updated.ZEITFENSTER_START),
      ZEITFENSTER_END: normalizeDateString(updated.ZEITFENSTER_END),
      curriculumIds: updated.curriculumIds || selectedBaustein?.curriculumIds || []
    }
    setSelectedBaustein(normalized)
    setBausteine((prev) =>
      prev.map((b) => (b.BID === normalized.BID ? { ...b, ...normalized } : b))
    )
    setAllBausteine((prev) =>
      prev.map((b) => (b.BID === normalized.BID ? { ...b, ...normalized } : b))
    )
    setPinnedBausteine((prev) =>
      prev.map((b) => (b.BID === normalized.BID ? { ...b, ...normalized } : b))
    )
  }

  const handleUpdateBaustein = async (field: string, value: any) => {
    if (!selectedBaustein) return

    try {
      const updateData = { [field]: value }
      const response = await apiClient.put(`/building-blocks/${selectedBaustein.BID}`, updateData)
      const data = response.data
      
      if (data.success) {
        applyBausteinUpdateToState({ ...selectedBaustein, ...data.data, [field]: value })
        toast.success('Baustein erfolgreich aktualisiert')
      } else {
        throw new Error(data.error || 'Failed to update Baustein')
      }
    } catch (error) {
      console.error('Error updating Baustein:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Aktualisieren: ${errorMessage}`)
    }
  }

  const DATE_UPDATE_CANCELLED = 'DATE_UPDATE_CANCELLED'

  const handleUpdateBausteinDate = async (field: 'START_DATUM' | 'END_DATUM', value: string) => {
    if (!selectedBaustein) return

    const normalizedNew = normalizeDateString(value)
    const normalizedOld = normalizeDateString(selectedBaustein[field])
    if (normalizedNew === normalizedOld) return

    const planCount = lernplaene.length
    const fieldLabel = field === 'START_DATUM' ? 'Startdatum' : 'Enddatum'

    let propagateToLearningPlans = false
    if (planCount > 0) {
      const confirmed = window.confirm(
        `Das ${fieldLabel} wurde geändert.\n\n` +
          `Soll dieser Wert auf alle ${planCount} Lernplan${planCount === 1 ? '' : 'e'} in diesem Baustein übertragen werden?\n\n` +
          '„OK“ = Baustein und alle Lernpläne aktualisieren\n' +
          '„Abbrechen“ = keine Änderung'
      )
      if (!confirmed) {
        throw new Error(DATE_UPDATE_CANCELLED)
      }
      propagateToLearningPlans = true
    }

    try {
      const response = await apiClient.put(`/building-blocks/${selectedBaustein.BID}`, {
        [field]: normalizedNew,
        propagateToLearningPlans
      })
      const data = response.data

      if (data.success) {
        applyBausteinUpdateToState({ ...selectedBaustein, ...data.data })
        if (propagateToLearningPlans && planCount > 0) {
          setLernplaene((prev) =>
            prev.map((lp) => ({
              ...lp,
              [field]: normalizedNew
            }))
          )
          const propagated = data.propagatedLearningPlans ?? planCount
          toast.success(
            `Baustein aktualisiert; ${fieldLabel} auf ${propagated} Lernplan${propagated === 1 ? '' : 'e'} übertragen`
          )
        } else {
          toast.success('Baustein erfolgreich aktualisiert')
        }
      } else {
        throw new Error(data.error || data.message || 'Failed to update Baustein')
      }
    } catch (error) {
      if (error instanceof Error && error.message === DATE_UPDATE_CANCELLED) {
        throw error
      }
      console.error('Error updating Baustein date:', error)
      const axiosDetail = getAxiosErrorMessage(error, '')
      if (axiosDetail.includes(LERNPLAN_DATE_RANGE_ERROR)) {
        toast.error(axiosDetail, { duration: 6000 })
      } else {
        const errorMessage = axiosDetail || (error instanceof Error ? error.message : 'Unbekannter Fehler')
        toast.error(`Fehler beim Aktualisieren: ${errorMessage}`)
      }
      throw error
    }
  }

  const handleClassAssignment = async (klasse: string, isAssigned: boolean) => {
    if (!selectedBaustein) return

    try {
      const response = isAssigned
        ? await apiClient.post(`/building-blocks/${selectedBaustein.BID}/classes`, { Klasse: klasse })
        : await apiClient.delete(`/building-blocks/${selectedBaustein.BID}/classes`, { data: { Klasse: klasse } })
      const data = response.data
      
      if (data.success) {
        // Update local zuordnungen state
        if (isAssigned) {
          setZuordnungen(prev => [...prev, { BID: selectedBaustein.BID.toString(), Klasse: klasse }])
        } else {
          setZuordnungen(prev => prev.filter(z => !(z.BID === selectedBaustein.BID.toString() && z.Klasse === klasse)))
        }
        
        toast.success(
          isAssigned 
            ? `Baustein wurde der Klasse ${klasse} zugeordnet` 
            : `Zuordnung zur Klasse ${klasse} wurde entfernt`
        )
      } else {
        throw new Error(data.error || 'Failed to update class assignment')
      }
    } catch (error) {
      console.error('Error updating class assignment:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Aktualisieren der Klassen-Zuordnung: ${errorMessage}`)
    }
  }

  const [updatingCompetencies, setUpdatingCompetencies] = useState(false)
  const [progressData, setProgressData] = useState<{
    totalBelege: number
    processedBelege: number
    updatedBelege: number
    totalEvidenceCreated: number
    totalEvidenceRemoved: number
    totalStatusUpdates: number
    totalSkillmapInitialized: number
    errors: number
  } | null>(null)

  const handleUpdateStudentCompetencies = async () => {
    if (!selectedBaustein) return

    // Show confirmation dialog
    const confirmed = window.confirm(
      'Für alle Schüler, die mit diesem Baustein verknüpft sind, werden die Kompetenzen aktualisiert.\n\n' +
      'Möchten Sie fortfahren?'
    )

    if (!confirmed) return

    try {
      setUpdatingCompetencies(true)
      setProgressData({
        totalBelege: 0,
        processedBelege: 0,
        updatedBelege: 0,
        totalEvidenceCreated: 0,
        totalEvidenceRemoved: 0,
        totalStatusUpdates: 0,
        totalSkillmapInitialized: 0,
        errors: 0
      })

      const response = await fetch(getApiUrl(`/building-blocks/${selectedBaustein.BID}/update-student-competencies`), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      if (!response.body) {
        throw new Error('Response body is null')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        
        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              
              if (data.data) {
                setProgressData({
                  totalBelege: data.data.totalBelege || 0,
                  processedBelege: data.data.processedBelege || 0,
                  updatedBelege: data.data.updatedBelege || 0,
                  totalEvidenceCreated: data.data.totalEvidenceCreated || 0,
                  totalEvidenceRemoved: data.data.totalEvidenceRemoved || 0,
                  totalStatusUpdates: data.data.totalStatusUpdates || 0,
                  totalSkillmapInitialized: data.data.totalSkillmapInitialized || 0,
                  errors: data.data.errors || 0
                })
              }

              if (data.completed) {
                const { totalBelege, updatedBelege, totalEvidenceCreated, totalEvidenceRemoved } = data.data || {}
                const removedText = totalEvidenceRemoved > 0 ? `, ${totalEvidenceRemoved} entfernt` : ''
                toast.success(
                  `Kompetenzen aktualisiert: ${updatedBelege} von ${totalBelege} Belegen bearbeitet, ${totalEvidenceCreated} Kompetenznachweise erstellt${removedText}`,
                  { duration: 5000 }
                )
                setProgressData(null)
              }

              if (data.error && data.completed) {
                throw new Error(data.error || 'Failed to update student competencies')
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError)
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating student competencies:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Aktualisieren der Schülerkompetenzen: ${errorMessage}`)
      setProgressData(null)
    } finally {
      setUpdatingCompetencies(false)
    }
  }

  const handleCloneBaustein = async (baustein: Baustein) => {
    // Bestätigungsdialog
    const confirmed = window.confirm(
      `Möchten Sie den Baustein "${baustein.NAME}" mit allen seinen Lernplänen klonen?\n\n` +
      `Der geklonte Baustein wird mit dem Namen "${baustein.NAME} (Kopie)" erstellt.`
    )
    
    if (!confirmed) return

    try {
      const response = await apiClient.post(`/building-blocks/${baustein.BID}/clone`, {
        newName: `${baustein.NAME} (Kopie)`,
        newYear: baustein.JAHR
      })
      const data = response.data
      
      if (data.success) {
        const clonedBaustein: Baustein = {
          ...data.data,
          COMPETENCY_COUNT: data.data?.COMPETENCY_COUNT ?? 0,
          curriculumIds: normalizeCurriculumIds(data.data?.curriculumIds || [])
        }
        
        // Add to local state
        setBausteine(prev => [...prev, clonedBaustein])
        setAllBausteine(prev => [...prev, clonedBaustein])
        toast.success(`Baustein "${baustein.NAME}" wurde erfolgreich mit allen Lernplänen geklont`)
        
        // Switch to the cloned Baustein and refresh data
        setSelectedBaustein(clonedBaustein)
        setEditForm(clonedBaustein)
        setPanelType('B')
        lastSavedCurriculumIdsRef.current = clonedBaustein.curriculumIds || []
        updateCurriculumSelection(clonedBaustein.curriculumIds || [], { skipSave: true })
        
        // Refresh Lernpläne and Zuordnungen for the cloned Baustein
        console.log(`Refreshing data for cloned Baustein BID: ${clonedBaustein.BID}`)
        await fetchLernplaene(clonedBaustein.BID)
        await fetchZuordnungen(clonedBaustein.BID)
        console.log('Data refresh completed')
      } else {
        throw new Error(data.error || 'Failed to clone Baustein')
      }
    } catch (error) {
      console.error('Error cloning Baustein:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Klonen des Bausteins: ${errorMessage}`)
    }
  }

  const isPinned = useCallback((bid: number) => {
    return pinnedBausteine.some((item) => item.BID === bid)
  }, [pinnedBausteine])

  const handleTogglePin = async (baustein: Baustein) => {
    try {
      if (isPinned(baustein.BID)) {
        await apiClient.delete(`/building-blocks/pins/${baustein.BID}`)
        toast.success('Baustein aus Favoriten entfernt')
      } else {
        await apiClient.post(`/building-blocks/pins/${baustein.BID}`, {})
        toast.success('Baustein zu Favoriten hinzugefügt')
      }
      await fetchPinnedBausteine()
    } catch (error) {
      console.error('Error toggling pin:', error)
      toast.error('Favorit konnte nicht aktualisiert werden')
    }
  }

  const handleCreateFolder = async () => {
    const name = window.prompt('Name des neuen Ordners:')
    if (!name || !name.trim()) return
    try {
      await apiClient.post('/building-blocks/folders', {
        name: name.trim(),
        parent_id: selectedFolderId
      })
      toast.success('Ordner erstellt')
      await fetchFolderTree()
      await fetchFolderContents(selectedFolderId)
    } catch (error) {
      console.error('Error creating folder:', error)
      toast.error('Ordner konnte nicht erstellt werden')
    }
  }

  const handleMoveBaustein = async (bid: number, folderId: number | null) => {
    try {
      await moveBausteinToFolder(bid, folderId)
      toast.success('Baustein verschoben')
    } catch (error) {
      console.error('Error moving baustein:', error)
      toast.error('Baustein konnte nicht verschoben werden')
    }
  }

  const handleDeleteBaustein = async (baustein: Baustein) => {
    // Bestätigungsdialog
    const confirmed = window.confirm(
      `Möchten Sie den Baustein "${baustein.NAME}" wirklich löschen?\n\n` +
      `Diese Aktion kann nicht rückgängig gemacht werden und löscht:\n` +
      `• Den Baustein selbst\n` +
      `• Alle zugehörigen Lernpläne\n` +
      `• Alle zugehörigen Aufgaben\n` +
      `• Alle Klassen-Zuordnungen`
    )
    
    if (!confirmed) return

    try {
      const response = await apiClient.delete(`/building-blocks/${baustein.BID}`)
      const data = response.data
      
      if (data.success) {
        // Remove from local state
        setBausteine(prev => prev.filter(b => b.BID !== baustein.BID))
        setAllBausteine(prev => prev.filter(b => b.BID !== baustein.BID))
        setFilteredBausteine(prev => prev.filter(b => b.BID !== baustein.BID))
        setPinnedBausteine(prev => prev.filter(b => b.BID !== baustein.BID))
        
        // If the deleted baustein was selected, go back to panel A
        if (selectedBaustein && selectedBaustein.BID === baustein.BID) {
          setSelectedBaustein(null)
          setEditForm({})
          setPanelType('A')
        }
        
        toast.success(`Baustein "${baustein.NAME}" wurde erfolgreich gelöscht`)
      } else {
        throw new Error(data.error || 'Failed to delete Baustein')
      }
    } catch (error) {
      console.error('Error deleting Baustein:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler'
      toast.error(`Fehler beim Löschen des Bausteins: ${errorMessage}`)
    }
  }

  const handleEditLernplan = async (lpid: string): Promise<LernplanDetail | null> => {
    try {
      const response = await apiClient.get(`/learning-plans/${lpid}`)

      if (response.data?.success) {
        const normalizeDate = (dateStr: string): string => {
          if (!dateStr) return ''
          if (dateStr.includes('T')) {
            return dateStr.split('T')[0]
          }
          return dateStr
        }

        const formattedData: LernplanDetail = {
          ...response.data.data,
          LPID: Number(response.data.data.LPID),
          START_DATUM: normalizeDate(response.data.data.START_DATUM || ''),
          END_DATUM: normalizeDate(response.data.data.END_DATUM || '')
        }

        setSelectedLernplanForEdit(formattedData)
        setEditLernplanForm(formattedData)
        setPanelType('C')
        await fetchTasks(parseInt(lpid, 10))
        return formattedData
      } else {
        throw new Error(response.data?.error || 'Failed to fetch Lernplan details')
      }
    } catch (error) {
      console.error('Error fetching Lernplan details:', error)
      toast.error(`Fehler beim Laden der Lernplan-Details: ${getAxiosErrorMessage(error, 'Unbekannter Fehler')}`)
      return null
    }
  }

  const handleExportLernplan = (lpid: string) => {
    try {
      const exportUrl = getApiUrl(`/learning-plans/${lpid}/export`)

      // Open export in new tab; cookies will be sent automatically
      const newWindow = window.open(exportUrl, '_blank')
      if (!newWindow) {
        // Fallback: direct navigation
        window.location.href = exportUrl
      }

      toast.success('Lernplan wird exportiert...')
    } catch (error) {
      console.error('Error exporting learning plan:', error)
      
      let errorMessage = 'Unbekannter Fehler beim Exportieren des Lernplans'
      let errorDetails = ''
      
      if (error instanceof Error) {
        if (error.message.includes('HTTP 401')) {
          errorMessage = 'Nicht angemeldet'
          errorDetails = 'Bitte melden Sie sich erneut an, um den Lernplan zu exportieren'
        } else if (error.message.includes('HTTP 404')) {
          errorMessage = 'Lernplan nicht gefunden'
          errorDetails = 'Der zu exportierende Lernplan existiert nicht mehr'
        } else if (error.message.includes('HTTP 403')) {
          errorMessage = 'Keine Berechtigung'
          errorDetails = 'Sie haben keine Berechtigung, diesen Lernplan zu exportieren'
        } else if (error.message.includes('HTTP 500')) {
          errorMessage = 'Serverfehler'
          errorDetails = 'Ein Fehler ist auf dem Server aufgetreten. Bitte versuchen Sie es später erneut'
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Verbindungsfehler'
          errorDetails = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung'
        } else {
          errorMessage = `Fehler beim Exportieren: ${error.message}`
        }
      }
      
      // Show detailed error message
      if (errorDetails) {
        toast.error(
          <div>
            <div className="font-semibold">{errorMessage}</div>
            <div className="text-sm mt-1">{errorDetails}</div>
          </div>,
          { duration: 6000 }
        )
      } else {
        toast.error(errorMessage, { duration: 6000 })
      }
    }
  }

  const handleExportLernplanSimple = (lpid: string) => {
    try {
      const exportUrl = getApiUrl(`/learning-plans/${lpid}/export-simple`)

      // Open export in new tab; cookies will be sent automatically
      const newWindow = window.open(exportUrl, '_blank')
      if (!newWindow) {
        // Fallback: direct navigation
        window.location.href = exportUrl
      }

      toast.success('Lernplan wird als einfache Liste exportiert...')
    } catch (error) {
      console.error('Error exporting simple learning plan:', error)
      
      let errorMessage = 'Unbekannter Fehler beim Exportieren des Lernplans'
      let errorDetails = ''
      
      if (error instanceof Error) {
        if (error.message.includes('HTTP 401')) {
          errorMessage = 'Nicht angemeldet'
          errorDetails = 'Bitte melden Sie sich erneut an, um den Lernplan zu exportieren'
        } else if (error.message.includes('HTTP 404')) {
          errorMessage = 'Lernplan nicht gefunden'
          errorDetails = 'Der zu exportierende Lernplan existiert nicht mehr'
        } else if (error.message.includes('HTTP 403')) {
          errorMessage = 'Keine Berechtigung'
          errorDetails = 'Sie haben keine Berechtigung, diesen Lernplan zu exportieren'
        } else if (error.message.includes('HTTP 500')) {
          errorMessage = 'Serverfehler'
          errorDetails = 'Ein Fehler ist auf dem Server aufgetreten. Bitte versuchen Sie es später erneut'
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Verbindungsfehler'
          errorDetails = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung'
        } else {
          errorMessage = `Fehler beim Exportieren: ${error.message}`
        }
      }
      
      // Show detailed error message
      if (errorDetails) {
        toast.error(
          <div>
            <div className="font-semibold">{errorMessage}</div>
            <div className="text-sm mt-1">{errorDetails}</div>
          </div>,
          { duration: 6000 }
        )
      } else {
        toast.error(errorMessage, { duration: 6000 })
      }
    }
  }

  const handleUpdateLernplan = async (lpid: string, field: string, value: any) => {
    if (field === 'START_DATUM' || field === 'END_DATUM') {
      const lp = lernplaene.find((item) => item.LPID === lpid)
      if (lp) {
        const start = field === 'START_DATUM' ? String(value) : (lp.START_DATUM || '')
        const end = field === 'END_DATUM' ? String(value) : (lp.END_DATUM || '')
        if (isLernplanEndBeforeStart(start, end)) {
          toast.error(LERNPLAN_DATE_RANGE_ERROR, { duration: 6000 })
          return
        }
      }
    }

    try {
      const response = await apiClient.put(`/learning-plans/${lpid}`, { [field]: value })
      const data = response.data
      
      if (data.success) {
        // Update the local state
        setLernplaene(prev => prev.map(lp => 
          lp.LPID === lpid ? { ...lp, [field]: value } : lp
        ))
        toast.success('Lernplan erfolgreich aktualisiert')
      } else {
        // Use detailed backend error message if available
        const errorMsg = data.message || data.error || 'Failed to update Lernplan'
        throw new Error(errorMsg)
      }
    } catch (error) {
      console.error('Error updating Lernplan:', error)

      const axiosDetail = getAxiosErrorMessage(error, '')
      const directMessage = error instanceof Error ? error.message : ''
      if (
        axiosDetail.includes(LERNPLAN_DATE_RANGE_ERROR) ||
        directMessage.includes(LERNPLAN_DATE_RANGE_ERROR)
      ) {
        toast.error(LERNPLAN_DATE_RANGE_ERROR, { duration: 6000 })
        return
      }
      
      let errorMessage = 'Unbekannter Fehler beim Aktualisieren des Lernplans'
      let errorDetails = ''
      
      if (error instanceof Error) {
        if (error.message.includes('Validation errors')) {
          errorMessage = 'Validierungsfehler beim Aktualisieren'
          errorDetails = 'Bitte überprüfen Sie die eingegebenen Daten auf Korrektheit'
        } else if (error.message.includes('HTTP 400')) {
          errorMessage = 'Ungültige Daten'
          errorDetails = 'Die eingegebenen Daten sind nicht gültig'
        } else if (error.message.includes('HTTP 401')) {
          errorMessage = 'Nicht angemeldet'
          errorDetails = 'Bitte melden Sie sich erneut an'
        } else if (error.message.includes('HTTP 403')) {
          errorMessage = 'Keine Berechtigung'
          errorDetails = 'Sie haben keine Berechtigung, diesen Lernplan zu bearbeiten'
        } else if (error.message.includes('HTTP 404')) {
          errorMessage = 'Lernplan nicht gefunden'
          errorDetails = 'Der zu bearbeitende Lernplan existiert nicht mehr'
        } else if (error.message.includes('HTTP 500')) {
          errorMessage = 'Serverfehler'
          errorDetails = 'Ein Fehler ist auf dem Server aufgetreten. Bitte versuchen Sie es später erneut'
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Verbindungsfehler'
          errorDetails = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung'
        } else {
          errorMessage = `Fehler beim Aktualisieren: ${error.message}`
        }
      }
      
      // Show detailed error message
      if (errorDetails) {
        toast.error(
          <div>
            <div className="font-semibold">{errorMessage}</div>
            <div className="text-sm mt-1">{errorDetails}</div>
          </div>,
          { duration: 6000 }
        )
      } else {
        toast.error(errorMessage, { duration: 6000 })
      }
    }
  }

  const handleCreateLernplan = async () => {
    if (!selectedBaustein) {
      toast.error('Kein Baustein ausgewählt')
      return
    }

    try {
      const newLernplanData = {
        NAME: 'Neuer Lernplan',
        FACH: selectedBaustein.FACH,
        STUFE: selectedBaustein.STUFE,
        JAHR: selectedBaustein.JAHR,
        BID: selectedBaustein.BID,
        START_DATUM: new Date().toISOString().split('T')[0],
        END_DATUM: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
        LERNZIELE: '',
        Diff: 'GK',
        STATUS: true
      }

      const response = await apiClient.post('/learning-plans', newLernplanData)
      const data = response.data
      
      if (data.success) {
        // Add the new Lernplan to the local state
        const newLernplan: Lernplan = {
          ...data.data,
          Eigentuemer: currentUsername,
          ERSTELLT_VON: currentUsername,
          BAUSTEIN_NAME: selectedBaustein.NAME
        }
        
        setLernplaene(prev => [...prev, newLernplan])
        toast.success('Lernplan erfolgreich erstellt')
        
        // Switch to edit mode for the new Lernplan
        const lernplanDetail: LernplanDetail = {
          LPID: parseInt(newLernplan.LPID),
          NAME: newLernplan.NAME,
          FACH: newLernplan.FACH,
          STUFE: newLernplan.STUFE,
          JAHR: newLernplan.JAHR,
          STATUS: newLernplan.STATUS,
          LERNZIELE: newLernplan.LERNZIELE,
          END_DATUM: newLernplan.END_DATUM,
          START_DATUM: newLernplan.START_DATUM,
          BID: newLernplan.BID,
          Diff: newLernplan.Diff
        }
        
        setSelectedLernplanForEdit(lernplanDetail)
        setEditLernplanForm(lernplanDetail)
        setPanelType('C')
      } else {
        // Use detailed backend error message if available
        let errorMsg = data.message || data.error || 'Failed to create Lernplan'
        
        // Extract specific validation errors if available
        if (data.details && Array.isArray(data.details)) {
          const validationErrors = data.details.map((err: any) => {
            switch (err.path) {
              case 'NAME':
                return 'Name ist erforderlich'
              case 'FACH':
                return 'Fach ist erforderlich'
              case 'STUFE':
                return 'Stufe ist erforderlich'
              case 'JAHR':
                return 'Jahr ist erforderlich'
              case 'START_DATUM':
                return 'Start Datum ist erforderlich oder hat ungültiges Format'
              case 'END_DATUM':
                return 'End Datum ist erforderlich oder hat ungültiges Format'
              case 'BID':
                return 'Baustein-ID ist erforderlich'
              case 'Diff':
                return 'Differenzierung muss GK, EK, FK oder WK sein'
              default:
                return err.msg || 'Ungültiger Wert'
            }
          })
          errorMsg = `${errorMsg}: ${validationErrors.join(', ')}`
        }
        
        throw new Error(errorMsg)
      }
    } catch (error) {
      console.error('Error creating Lernplan:', error)
      
      let errorMessage = 'Unbekannter Fehler beim Erstellen des Lernplans'
      let errorDetails = ''
      
      if (error instanceof Error) {
        // Check for specific error types
        if (error.message.includes('Validation errors')) {
          errorMessage = 'Validierungsfehler beim Erstellen des Lernplans'
          errorDetails = 'Bitte überprüfen Sie alle Pflichtfelder (Name, Fach, Stufe, Jahr)'
        } else if (error.message.includes('HTTP 400')) {
          errorMessage = 'Ungültige Daten für den Lernplan'
          errorDetails = 'Bitte überprüfen Sie alle Eingabefelder auf Korrektheit'
        } else if (error.message.includes('HTTP 401')) {
          errorMessage = 'Nicht angemeldet'
          errorDetails = 'Bitte melden Sie sich erneut an'
        } else if (error.message.includes('HTTP 403')) {
          errorMessage = 'Keine Berechtigung'
          errorDetails = 'Sie haben keine Berechtigung, Lernpläne zu erstellen'
        } else if (error.message.includes('HTTP 500')) {
          errorMessage = 'Serverfehler'
          errorDetails = 'Ein Fehler ist auf dem Server aufgetreten. Bitte versuchen Sie es später erneut'
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Verbindungsfehler'
          errorDetails = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung'
        } else {
          errorMessage = `Fehler beim Erstellen: ${error.message}`
        }
      }
      
      // Show detailed error message
      if (errorDetails) {
        toast.error(
          <div>
            <div className="font-semibold">{errorMessage}</div>
            <div className="text-sm mt-1">{errorDetails}</div>
          </div>,
          { duration: 6000 }
        )
      } else {
        toast.error(errorMessage, { duration: 6000 })
      }
    }
  }

  const handleDeleteLernplan = async (lpid: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Lernplan löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return
    }

    try {
      const response = await apiClient.delete(`/learning-plans/${lpid}`)
      const data = response.data
      
      if (data.success) {
        // Remove from local state
        setLernplaene(prev => prev.filter(lp => lp.LPID !== lpid))
        toast.success('Lernplan erfolgreich gelöscht')
      } else {
        // Use detailed backend error message if available
        const errorMsg = data.message || data.error || 'Failed to delete Lernplan'
        throw new Error(errorMsg)
      }
    } catch (error) {
      console.error('Error deleting Lernplan:', error)
      
      let errorMessage = 'Unbekannter Fehler beim Löschen des Lernplans'
      let errorDetails = ''
      
      if (error instanceof Error) {
        if (error.message.includes('HTTP 404')) {
          errorMessage = 'Lernplan nicht gefunden'
          errorDetails = 'Der zu löschende Lernplan existiert nicht mehr'
        } else if (error.message.includes('HTTP 401')) {
          errorMessage = 'Nicht angemeldet'
          errorDetails = 'Bitte melden Sie sich erneut an'
        } else if (error.message.includes('HTTP 403')) {
          errorMessage = 'Keine Berechtigung'
          errorDetails = 'Sie haben keine Berechtigung, diesen Lernplan zu löschen'
        } else if (error.message.includes('HTTP 500')) {
          errorMessage = 'Serverfehler'
          errorDetails = 'Ein Fehler ist auf dem Server aufgetreten. Bitte versuchen Sie es später erneut'
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Verbindungsfehler'
          errorDetails = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung'
        } else {
          errorMessage = `Fehler beim Löschen: ${error.message}`
        }
      }
      
      // Show detailed error message
      if (errorDetails) {
        toast.error(
          <div>
            <div className="font-semibold">{errorMessage}</div>
            <div className="text-sm mt-1">{errorDetails}</div>
          </div>,
          { duration: 6000 }
        )
      } else {
        toast.error(errorMessage, { duration: 6000 })
      }
    }
  }

  const handleCloneLernplan = async (lpid: string) => {
    if (!confirm('Möchten Sie diesen Lernplan klonen? Alle Aufgaben werden ebenfalls dupliziert.')) {
      return
    }

    try {
      const response = await apiClient.post(`/learning-plans/${lpid}/clone`)
      const data = response.data
      
      if (data.success) {
        // Add the new cloned Lernplan to the local state
        const newLernplan: Lernplan = {
          ...data.data,
          Eigentuemer: currentUsername,
          BAUSTEIN_NAME: selectedBaustein?.NAME || ''
        }
        
        setLernplaene(prev => [...prev, newLernplan])
        toast.success(
          <div>
            <div className="font-semibold">Lernplan erfolgreich geklont</div>
            <div className="text-sm mt-1">
              {data.clonedTasks} Aufgaben wurden dupliziert
            </div>
          </div>,
          { duration: 6000 }
        )
      } else {
        // Use detailed backend error message if available
        const errorMsg = data.message || data.error || 'Failed to clone Lernplan'
        throw new Error(errorMsg)
      }
    } catch (error) {
      console.error('Error cloning Lernplan:', error)
      
      let errorMessage = 'Unbekannter Fehler beim Klonen des Lernplans'
      let errorDetails = ''
      
      if (error instanceof Error) {
        if (error.message.includes('HTTP 404')) {
          errorMessage = 'Lernplan nicht gefunden'
          errorDetails = 'Der zu klonende Lernplan existiert nicht mehr'
        } else if (error.message.includes('HTTP 401')) {
          errorMessage = 'Nicht angemeldet'
          errorDetails = 'Bitte melden Sie sich erneut an'
        } else if (error.message.includes('HTTP 403')) {
          errorMessage = 'Keine Berechtigung'
          errorDetails = 'Sie haben keine Berechtigung, diesen Lernplan zu klonen'
        } else if (error.message.includes('HTTP 500')) {
          errorMessage = 'Serverfehler'
          errorDetails = 'Ein Fehler ist auf dem Server aufgetreten. Bitte versuchen Sie es später erneut'
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Verbindungsfehler'
          errorDetails = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung'
        } else {
          errorMessage = `Fehler beim Klonen: ${error.message}`
        }
      }
      
      // Show detailed error message
      if (errorDetails) {
        toast.error(
          <div>
            <div className="font-semibold">{errorMessage}</div>
            <div className="text-sm mt-1">{errorDetails}</div>
          </div>,
          { duration: 6000 }
        )
      } else {
        toast.error(errorMessage, { duration: 6000 })
      }
    }
  }

  const handleBackToPanelB = () => {
    setPanelType('B')
    setSelectedLernplanForEdit(null)
    setEditLernplanForm({})
    setTasks([])
  }

  const handleCreateBaustein = async () => {
    try {
      const newBausteinData = {
        NAME: 'Neuer Baustein',
        FACH: 'Deutsch',
        STUFE: '5',
        JAHR: new Date().getFullYear(),
        START_DATUM: new Date().toISOString().split('T')[0],
        END_DATUM: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
        INFO: '',
        Eigentuemer: currentUsername,
        folder_id: selectedFolderId,
        curriculumIds: []
      }

      const response = await apiClient.post('/building-blocks', newBausteinData)
      const data = response.data
      
      if (data.success) {
        // Add the new Baustein to the local state
        const newBaustein: Baustein = {
          ...data.data,
          Eigentuemer: currentUsername,
          COMPETENCY_COUNT: data.data?.COMPETENCY_COUNT ?? 0,
          curriculumIds: normalizeCurriculumIds(data.data?.curriculumIds || [])
        }
        
        setBausteine(prev => [...prev, newBaustein])
        setAllBausteine(prev => [...prev, newBaustein])
        toast.success('Baustein erfolgreich erstellt')
        
        // Switch to edit mode for the new Baustein
        setSelectedBaustein(newBaustein)
        setEditForm(newBaustein)
        setPanelType('B')
        lastSavedCurriculumIdsRef.current = newBaustein.curriculumIds || []
        updateCurriculumSelection(newBaustein.curriculumIds || [], { skipSave: true })
      } else {
        // Use detailed backend error message if available
        let errorMsg = data.message || data.error || 'Failed to create Baustein'
        
        // Extract specific validation errors if available
        if (data.details && Array.isArray(data.details)) {
          const validationErrors = data.details.map((err: any) => {
            switch (err.path) {
              case 'NAME':
                return 'Name ist erforderlich'
              case 'FACH':
                return 'Fach ist erforderlich'
              case 'STUFE':
                return 'Stufe ist erforderlich'
              case 'JAHR':
                return 'Jahr ist erforderlich und muss zwischen 2020 und 2030 liegen'
              case 'START_DATUM':
                return 'Start Datum ist erforderlich oder hat ungültiges Format'
              case 'END_DATUM':
                return 'End Datum ist erforderlich oder hat ungültiges Format'
              default:
                return err.msg || 'Ungültiger Wert'
            }
          })
          errorMsg = `${errorMsg}: ${validationErrors.join(', ')}`
        }
        
        throw new Error(errorMsg)
      }
    } catch (error) {
      console.error('Error creating Baustein:', error)
      
      let errorMessage = 'Unbekannter Fehler beim Erstellen des Bausteins'
      let errorDetails = ''
      
      if (error instanceof Error) {
        if (error.message.includes('Validation failed')) {
          errorMessage = 'Validierungsfehler beim Erstellen des Bausteins'
          errorDetails = 'Bitte überprüfen Sie alle Pflichtfelder (Name, Fach, Stufe, Jahr, Start Datum, End Datum)'
        } else if (error.message.includes('HTTP 400')) {
          errorMessage = 'Ungültige Daten für den Baustein'
          errorDetails = 'Bitte überprüfen Sie alle Eingabefelder auf Korrektheit'
        } else if (error.message.includes('HTTP 401')) {
          errorMessage = 'Nicht angemeldet'
          errorDetails = 'Bitte melden Sie sich erneut an'
        } else if (error.message.includes('HTTP 403')) {
          errorMessage = 'Keine Berechtigung'
          errorDetails = 'Sie haben keine Berechtigung, Bausteine zu erstellen'
        } else if (error.message.includes('HTTP 500')) {
          errorMessage = 'Serverfehler'
          errorDetails = 'Ein Fehler ist auf dem Server aufgetreten. Bitte versuchen Sie es später erneut'
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Verbindungsfehler'
          errorDetails = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung'
        } else {
          errorMessage = `Fehler beim Erstellen: ${error.message}`
        }
      }
      
      // Show detailed error message
      if (errorDetails) {
        toast.error(
          <div>
            <div className="font-semibold">{errorMessage}</div>
            <div className="text-sm mt-1">{errorDetails}</div>
          </div>,
          { duration: 6000 }
        )
      } else {
        toast.error(errorMessage, { duration: 6000 })
      }
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = tasks.findIndex((task) => task.AID === active.id)
      const newIndex = tasks.findIndex((task) => task.AID === over.id)

      const newTasks = arrayMove(tasks, oldIndex, newIndex)
      updateTaskOrder(newTasks)
    }
  }

  const handleLernplanDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = lernplaene.findIndex((lernplan) => lernplan.LPID === active.id)
      const newIndex = lernplaene.findIndex((lernplan) => lernplan.LPID === over.id)

      const newLernplaene = arrayMove(lernplaene, oldIndex, newIndex)
      updateLernplanOrder(newLernplaene)
    }
  }

  const handleLernplanInputChange = (field: keyof LernplanDetail, value: any) => {
    setEditLernplanForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSaveLernplan = async () => {
    if (!selectedLernplanForEdit) return

    try {
      setSavingLernplan(true)
      
      // Validate required fields - only validate fields that are visible in the form
      const missingFields = []
      
      if (!editLernplanForm.NAME?.trim()) {
        missingFields.push('Name')
      }
      if (!editLernplanForm.START_DATUM?.trim()) {
        missingFields.push('Start Datum')
      }
      if (!editLernplanForm.END_DATUM?.trim()) {
        missingFields.push('End Datum')
      }
      // Differenzierung has a default value, so it's always valid
      // Lernziele is optional based on the screenshot
      
      if (missingFields.length > 0) {
        toast.error(
          <div>
            <div className="font-semibold">Pflichtfelder fehlen:</div>
            <div className="text-sm mt-1">{missingFields.join(', ')}</div>
          </div>,
          { duration: 6000 }
        )
        return
      }

      if (
        isLernplanEndBeforeStart(
          editLernplanForm.START_DATUM || '',
          editLernplanForm.END_DATUM || ''
        )
      ) {
        toast.error(LERNPLAN_DATE_RANGE_ERROR, { duration: 6000 })
        return
      }
      
      // Filter out null/undefined values to avoid validation errors
      const updateData: any = { ...editLernplanForm }
      Object.keys(updateData).forEach((key: string) => {
        if (updateData[key] === null || updateData[key] === undefined || updateData[key] === '') {
          delete updateData[key]
        }
      })
      
      console.log('Sending Lernplan update:', {
        lpid: selectedLernplanForEdit.LPID,
        data: updateData
      })
      
      const response = await apiClient.put(`/learning-plans/${selectedLernplanForEdit.LPID}`, updateData)
      const data = response.data
      
      if (data.success) {
        toast.success('Lernplan erfolgreich gespeichert')
        setPanelType('B')
        setSelectedLernplanForEdit(null)
        setEditLernplanForm({})
        // Refresh the Lernpläne list
        if (selectedBaustein) {
          await fetchLernplaene(selectedBaustein.BID)
        }
      } else {
        throw new Error(data.error || 'Failed to update Lernplan')
      }
    } catch (error) {
      console.error('Error updating Lernplan:', error)
      const axiosDetail = getAxiosErrorMessage(error, '')
      const directMessage = error instanceof Error ? error.message : ''
      if (
        axiosDetail.includes(LERNPLAN_DATE_RANGE_ERROR) ||
        directMessage.includes(LERNPLAN_DATE_RANGE_ERROR)
      ) {
        toast.error(LERNPLAN_DATE_RANGE_ERROR, { duration: 6000 })
        return
      }
      const errorMessage = axiosDetail || directMessage || 'Unbekannter Fehler'
      toast.error(`Fehler beim Speichern der Lernplan-Änderungen: ${errorMessage}`)
    } finally {
      setSavingLernplan(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      // Für Sortierung immer aufsteigend, für andere Felder absteigend
      setSortDirection(field === 'Sortierung' ? 'asc' : 'desc')
    }
  }

  const iconSortOptions: Array<{ value: SortField; label: string }> = [
    { value: 'NAME', label: 'Name' },
    { value: 'FACH', label: 'Fach' },
    { value: 'STUFE', label: 'Stufe' },
    { value: 'JAHR', label: 'Jahr' },
    { value: 'START_DATUM', label: 'Startdatum' },
    { value: 'END_DATUM', label: 'Enddatum' },
    { value: 'PERF_STUDENT_COUNT', label: 'Schüler' },
    { value: 'PERF_AVG_PCT', label: 'Ø Bearbeitung' }
  ]

  const handleIconSortFieldChange = (field: SortField) => {
    setSortField(field)
    setSortDirection(
      field === 'NAME' || field === 'FACH' || field === 'STUFE' || field === 'JAHR'
        ? 'asc'
        : 'desc'
    )
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-primary-600" />
      : <ArrowDown className="w-4 h-4 text-primary-600" />
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      // Handle ISO-String format (YYYY-MM-DDTHH:mm:ss.sssZ) - extract date part only
      let datePart = dateString
      if (dateString.includes('T')) {
        datePart = dateString.split('T')[0]
      }
      
      // Handle pure date format (YYYY-MM-DD)
      const parts = datePart.split('-')
      if (parts.length === 3 && parts[0].length === 4) {
        // Format: YYYY-MM-DD -> DD.MM.YYYY
        const day = parts[2].padStart(2, '0')
        const month = parts[1].padStart(2, '0')
        const year = parts[0]
        return `${day}.${month}.${year}`
      }
      
      // Fallback: return as-is if format is unexpected
      return dateString
    } catch {
      return dateString
    }
  }

  const renderFolderTree = (nodes: BausteinFolder[], level = 0): any[] =>
    nodes.flatMap((folder) => {
      const isSelected = selectedFolderId === folder.id
      return [
        <div key={`folder-${folder.id}`}>
          <button
            type="button"
            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${folderIndentClasses[Math.min(level, folderIndentClasses.length - 1)]} ${
              isSelected
                ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/40 dark:text-primary-100'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'
            }`}
            onClick={() => {
              setSelectedFolderId(folder.id)
              setPinFilter('all')
            }}
            onDragOver={(e) => {
              e.preventDefault()
            }}
            onDrop={(e) => {
              e.preventDefault()
              const bid = Number(e.dataTransfer.getData('text/plain'))
              if (Number.isInteger(bid) && bid > 0) {
                void handleMoveBaustein(bid, folder.id)
              }
            }}
          >
            {isSelected ? (
              <FolderOpen className="h-4 w-4 text-amber-500 dark:text-amber-400" aria-hidden />
            ) : (
              <Folder className="h-4 w-4 text-amber-500 dark:text-amber-400" aria-hidden />
            )}
            <span className="truncate">{folder.name}</span>
          </button>
        </div>,
        ...(folder.children?.length ? renderFolderTree(folder.children, level + 1) : [])
      ]
    })

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary-600 dark:text-primary-400" aria-hidden />
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Lade Bausteine…</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="text-center">
          <p className="mb-4 text-lg font-medium text-red-600 dark:text-red-400">Fehler beim Laden der Daten</p>
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">{error}</p>
          <Button type="button" onClick={() => { setError(null); fetchBausteine() }}>
            Erneut versuchen
          </Button>
        </div>
      </div>
    )
  }

    return (
    <div className="flex w-full flex-col gap-6 pb-8">
        {/* Header - Kompakt bei Panel B und C */}
        {panelType === 'B' ? (
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                    {selectedBaustein ? selectedBaustein.NAME : 'Baustein Details'}
              </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedBaustein?.FACH} - {selectedBaustein?.STUFE} ({selectedBaustein?.JAHR})
              </p>
                </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={handleBackToPanelA}>
                ← Zurück zu Bausteinen
            </Button>
          </div>
                </div>
        ) : panelType === 'C' ? (
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                  <div>
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                    {selectedLernplanForEdit ? selectedLernplanForEdit.NAME : 'Lernplan bearbeiten'}
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedBaustein?.NAME} - {selectedLernplanForEdit?.FACH}
                  </p>
                  </div>
                  </div>
            <Button type="button" variant="outline" size="sm" onClick={handleBackToPanelB}>
                ← Zurück zu Lernplänen
            </Button>
          </div>
        </div>
        ) : null}

        {panelType === 'A' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(200px,260px)_minmax(0,1fr)] lg:items-start">
            <Tile className="h-fit lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
              <div
                className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/70"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  const bid = Number(e.dataTransfer.getData('text/plain'))
                  if (Number.isInteger(bid) && bid > 0) {
                    void handleMoveBaustein(bid, null)
                  }
                }}
              >
                <button
                  type="button"
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${
                    selectedFolderId == null && pinFilter === 'all'
                      ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/40 dark:text-primary-100'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'
                  }`}
                  onClick={() => {
                    setSelectedFolderId(null)
                    setPinFilter('all')
                  }}
                >
                  <Folder className="h-4 w-4 text-amber-500 dark:text-amber-400" aria-hidden />
                  Alle Bausteine
                </button>
                <button
                  type="button"
                  className={`mt-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${
                    pinFilter === 'pins'
                      ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/40 dark:text-primary-100'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'
                  }`}
                  onClick={() => setPinFilter('pins')}
                >
                  <Star className="h-4 w-4 text-yellow-500 dark:text-yellow-400" aria-hidden />
                  Meine Pins
                </button>
              </div>
              <div className="space-y-1 p-2">{renderFolderTree(folderTree)}</div>
            </Tile>

            <div className="space-y-3">
              <ListToolbar
                title={pinFilter === 'pins' ? 'Meine angepinnten Bausteine' : (currentFolder?.name || 'Bausteine')}
                className="mb-2 gap-2 sm:items-center"
                trailingClassName="w-full min-w-0 flex-1 justify-end gap-1.5 sm:max-w-none lg:flex-nowrap"
                trailing={
                  <>
                    <ViewModeToggle
                      mode={viewMode}
                      onChange={(mode) => {
                        setViewModeState(mode)
                        setBausteinViewMode(mode)
                      }}
                    />
                    {viewMode === 'icons' && (
                      <>
                        <label htmlFor="bb-icon-sort-field" className="sr-only">
                          Sortierung
                        </label>
                        <select
                          id="bb-icon-sort-field"
                          value={sortField}
                          onChange={(e) => handleIconSortFieldChange(e.target.value as SortField)}
                          className="input-field min-h-[2.25rem] w-[8.5rem] py-2 text-sm"
                          title="Sortierung"
                        >
                          {iconSortOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                          title={sortDirection === 'asc' ? 'Aufsteigend' : 'Absteigend'}
                        >
                          {sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" aria-hidden /> : <ArrowDown className="h-4 w-4" aria-hidden />}
                        </Button>
                      </>
                    )}
                    <label htmlFor="bb-global-search" className="sr-only">
                      Globale Suche
                    </label>
                    <div className="relative w-full min-w-[10rem] max-w-[14rem] flex-1 sm:max-w-[12rem] lg:max-w-[14rem] lg:flex-none">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" aria-hidden />
                      <input
                        id="bb-global-search"
                        type="search"
                        className="input-field min-h-[2.25rem] w-full py-2 pl-9 text-sm"
                        placeholder="Suche…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        title="Globale Suche: Name, Fach, Stufe …"
                      />
                    </div>
                    {isAdmin && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRecalculateStats}
                        disabled={recalculating}
                        title="Performance-Statistiken neu berechnen"
                      >
                        {recalculating ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <RefreshCw className="h-4 w-4" aria-hidden />}
                        <span className="hidden xl:inline">{recalculating ? 'Berechnung…' : 'Statistiken'}</span>
                      </Button>
                    )}
                    <Button type="button" variant="outline" size="sm" onClick={handleCreateFolder} title="Neuer Ordner">
                      <Folder className="h-4 w-4" aria-hidden />
                      <span className="hidden sm:inline">Neuer Ordner</span>
                    </Button>
                    <Button type="button" size="sm" onClick={handleCreateBaustein} title="Neuer Baustein">
                      <Plus className="h-4 w-4" aria-hidden />
                      <span className="hidden sm:inline">Neuer Baustein</span>
                    </Button>
                  </>
                }
              />

              {viewMode === 'icons' ? (
                <div className="space-y-3">
                  {pinFilter !== 'pins' && selectedFolderId !== null && (
                    <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200">
                      Bausteine in diesem Ordner: Über den Button <strong>„Aus Ordner“</strong> auf der Kachel können Sie einen Baustein direkt wieder nach „Alle Bausteine“ verschieben.
                    </div>
                  )}
                  <div className="grid auto-rows-fr grid-cols-2 items-stretch gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {pinFilter !== 'pins' &&
                    currentSubfolders.map((folder) => (
                      <FolderTile
                        key={`tile-folder-${folder.id}`}
                        name={folder.name}
                        countLabel={`${Number(folder.bausteine_count || 0)} Bausteine`}
                        onClick={() => {
                          setSelectedFolderId(folder.id)
                          setPinFilter('all')
                        }}
                        onDropBaustein={(bid) => {
                          void handleMoveBaustein(bid, folder.id)
                        }}
                      />
                    ))}
                  {sortedBausteine.map((baustein) => (
                    <div key={`tile-bb-${baustein.BID}`} className="relative h-full min-h-48">
                      {(() => {
                        const bPerf = performanceStats?.bausteine.find(p => p.BID === baustein.BID)
                        const studentLabel =
                          bPerf && (bPerf.access_count > 0 || bPerf.student_count > 0)
                            ? (bPerf.access_count > 0 ? `${bPerf.student_count}/${bPerf.access_count}` : `${bPerf.student_count}`)
                            : undefined
                        const avgCompletionLabel =
                          bPerf && (bPerf.access_count > 0 || bPerf.student_count > 0)
                            ? `${Number(bPerf.avg_completion_pct).toFixed(0)}%`
                            : undefined

                        return (
                          <BausteinTile
                            name={baustein.NAME}
                            fach={baustein.FACH}
                            stufe={baustein.STUFE}
                            jahr={baustein.JAHR}
                            startDate={formatDate(getBausteinZeitfensterStart(baustein))}
                            endDate={formatDate(getBausteinZeitfensterEnd(baustein))}
                            studentLabel={studentLabel}
                            avgCompletionLabel={avgCompletionLabel}
                            active={bausteinHatAktiveLernplaene(baustein)}
                            onClick={() => handleRowClick(baustein)}
                            draggable={pinFilter !== 'pins'}
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', String(baustein.BID))
                            }}
                          />
                        )
                      })()}
                      <button
                        type="button"
                        className="absolute right-2 top-2 rounded-full bg-white/90 p-1 text-yellow-500 shadow-sm hover:bg-white dark:bg-gray-900/90"
                        onClick={(e) => {
                          e.stopPropagation()
                          void handleTogglePin(baustein)
                        }}
                        title={isPinned(baustein.BID) ? 'Pin entfernen' : 'Anpinnen'}
                      >
                        <Star className={`h-4 w-4 ${isPinned(baustein.BID) ? 'fill-current' : ''}`} />
                      </button>
                      {pinFilter !== 'pins' && selectedFolderId !== null && (
                        <button
                          type="button"
                          className="absolute left-2 top-2 rounded-md bg-white/90 px-2 py-0.5 text-[11px] font-medium text-gray-700 shadow-sm hover:bg-white dark:bg-gray-900/90 dark:text-gray-200"
                          onClick={(e) => {
                            e.stopPropagation()
                            void handleMoveBaustein(baustein.BID, null)
                          }}
                          title="Aus Ordner entfernen"
                        >
                          Aus Ordner
                        </button>
                      )}
                    </div>
                  ))}
                  </div>
                </div>
              ) : (
                <>
                  <BreadcrumbBar items={breadcrumbItems} onSelect={(id) => {
                    setSelectedFolderId(id)
                    setPinFilter('all')
                  }} />
                  <TableScrollArea>
                    <Table>
                    <TableHead>
                      <TableRow>
                        <SortableTh sortKey="FACH" className="w-36" activeKey={sortField} direction={sortDirection} onSort={(k) => handleSort(k as SortField)}>Fach</SortableTh>
                        <SortableTh sortKey="JAHR" className="w-16" activeKey={sortField} direction={sortDirection} onSort={(k) => handleSort(k as SortField)}>Jahr</SortableTh>
                        <SortableTh sortKey="COMPETENCY_COUNT" className="w-20" activeKey={sortField} direction={sortDirection} onSort={(k) => handleSort(k as SortField)}>Kompetenzen</SortableTh>
                        <SortableTh sortKey="STUFE" className="w-16" activeKey={sortField} direction={sortDirection} onSort={(k) => handleSort(k as SortField)}>Stufe</SortableTh>
                        <SortableTh sortKey="NAME" activeKey={sortField} direction={sortDirection} onSort={(k) => handleSort(k as SortField)}>Name</SortableTh>
                        <SortableTh sortKey="START_DATUM" className="w-28" activeKey={sortField} direction={sortDirection} onSort={(k) => handleSort(k as SortField)} title="Frühestes Startdatum der Lernpläne">Start (Lernpläne)</SortableTh>
                        <SortableTh sortKey="END_DATUM" className="w-28" activeKey={sortField} direction={sortDirection} onSort={(k) => handleSort(k as SortField)} title="Spätestes Enddatum der Lernpläne">Ende (Lernpläne)</SortableTh>
                        <SortableTh sortKey="PERF_STUDENT_COUNT" className="w-20" activeKey={sortField} direction={sortDirection} onSort={(k) => handleSort(k as SortField)}>
                          <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" aria-hidden />Schüler</span>
                        </SortableTh>
                        <SortableTh sortKey="PERF_AVG_PCT" className="w-24" activeKey={sortField} direction={sortDirection} onSort={(k) => handleSort(k as SortField)}>
                          <span className="inline-flex items-center gap-1"><BarChart2 className="h-3 w-3" aria-hidden />Ø Bearb.</span>
                        </SortableTh>
                        <TableHeaderCell className="w-32">Aktionen</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pinFilter !== 'pins' && searchTerm.trim() === '' && currentSubfolders.map((folder) => (
                        <TableRow
                          key={`folder-row-${folder.id}`}
                          className="cursor-pointer bg-amber-50/70 hover:bg-amber-100/80 dark:bg-amber-950/20 dark:hover:bg-amber-950/30"
                          onClick={() => {
                            setSelectedFolderId(folder.id)
                            setPinFilter('all')
                          }}
                          onDragOver={(e) => {
                            e.preventDefault()
                          }}
                          onDrop={(e) => {
                            e.preventDefault()
                            const bid = Number(e.dataTransfer.getData('text/plain'))
                            if (Number.isInteger(bid) && bid > 0) {
                              void handleMoveBaustein(bid, folder.id)
                            }
                          }}
                        >
                          <TableCell colSpan={5} className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                            <span className="inline-flex items-center gap-2">
                              <Folder className="h-4 w-4 text-amber-500 dark:text-amber-400" aria-hidden />
                              {folder.name}
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({Number(folder.bausteine_count || 0)} Bausteine)
                              </span>
                            </span>
                          </TableCell>
                          <TableCell className="px-3 py-4 text-sm text-gray-400 dark:text-gray-500">-</TableCell>
                          <TableCell className="px-3 py-4 text-sm text-gray-400 dark:text-gray-500">-</TableCell>
                          <TableCell className="px-3 py-4 text-sm text-gray-400 dark:text-gray-500">-</TableCell>
                          <TableCell className="px-3 py-4 text-sm text-gray-400 dark:text-gray-500">-</TableCell>
                          <TableCell className="px-3 py-4 whitespace-nowrap text-sm">
                            <Button type="button" size="xs" variant="outline">
                              Öffnen
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {sortedBausteine.length > 0 ? (
                        sortedBausteine.map((baustein) => {
                          const competencyCount = baustein.COMPETENCY_COUNT ?? 0
                          const hatAktiveLernplaene = bausteinHatAktiveLernplaene(baustein)
                          return (
                            <TableRow
                              key={baustein.BID}
                              className={
                                hatAktiveLernplaene
                                  ? 'cursor-pointer lr-baustein-active-row'
                                  : 'cursor-pointer'
                              }
                              onClick={() => handleRowClick(baustein)}
                              draggable={pinFilter !== 'pins'}
                              onDragStart={(e) => {
                                e.dataTransfer.setData('text/plain', String(baustein.BID))
                              }}
                            >
                              <TableCell className="px-4 py-4 whitespace-nowrap">
                                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  baustein.FACH === 'Deutsch'
                                    ? baustein.Eigentuemer !== 'alle'
                                      ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200'
                                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-200'
                                    : baustein.FACH === 'Mathematik'
                                      ? baustein.Eigentuemer !== 'alle'
                                        ? 'bg-blue-200 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
                                        : 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                }`}>
                                  {baustein.FACH}
                                </div>
                              </TableCell>
                              <TableCell className="px-3 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-gray-100">{baustein.JAHR}</TableCell>
                              <TableCell className="px-3 py-4 whitespace-nowrap text-center text-sm">
                                {competencyCount > 0 ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200">
                                    {competencyCount}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center text-red-500">
                                    <X className="h-4 w-4" />
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="px-3 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-gray-100">{baustein.STUFE}</TableCell>
                              <TableCell className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100 max-w-xs break-words">{baustein.NAME}</TableCell>
                              <TableCell className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatDate(getBausteinZeitfensterStart(baustein))}</TableCell>
                              <TableCell className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatDate(getBausteinZeitfensterEnd(baustein))}</TableCell>
                              {(() => {
                                const bPerf = performanceStats?.bausteine.find(p => p.BID === baustein.BID)
                                return (
                                  <>
                                    <TableCell className="px-3 py-4 whitespace-nowrap text-sm">
                                      {bPerf && (bPerf.access_count > 0 || bPerf.student_count > 0) ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 dark:border-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200">
                                          {bPerf.access_count > 0 ? `${bPerf.student_count}/${bPerf.access_count}` : bPerf.student_count}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400 dark:text-gray-500 text-xs">–</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="px-3 py-4 whitespace-nowrap text-sm">
                                      {bPerf && (bPerf.access_count > 0 || bPerf.student_count > 0) ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200 dark:border-green-800 dark:bg-green-950/50 dark:text-green-200">
                                          {Number(bPerf.avg_completion_pct).toFixed(0)}%
                                        </span>
                                      ) : (
                                        <span className="text-gray-400 dark:text-gray-500 text-xs">–</span>
                                      )}
                                    </TableCell>
                                  </>
                                )
                              })()}
                              <TableCell className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      void handleTogglePin(baustein)
                                    }}
                                    className="p-2 rounded-full transition-colors text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-950/30"
                                    title={isPinned(baustein.BID) ? 'Pin entfernen' : 'Anpinnen'}
                                  >
                                    <Star className={`h-4 w-4 ${isPinned(baustein.BID) ? 'fill-current' : ''}`} />
                                  </button>
                                  {pinFilter !== 'pins' && selectedFolderId !== null && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        void handleMoveBaustein(baustein.BID, null)
                                      }}
                                      className="p-2 rounded-full transition-colors text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:text-amber-200 dark:hover:bg-amber-950/30"
                                      title="Aus Ordner entfernen"
                                    >
                                      <Folder className="h-4 w-4" aria-hidden />
                                      <span className="sr-only">Aus Ordner entfernen</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleRowClick(baustein)
                                    }}
                                    disabled={baustein.Eigentuemer !== 'alle' && baustein.Eigentuemer !== currentUsername}
                                    className={`p-2 rounded-full transition-colors ${
                                      baustein.Eigentuemer !== 'alle' && baustein.Eigentuemer !== currentUsername
                                        ? 'text-gray-300 cursor-not-allowed'
                                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100'
                                    }`}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleCloneBaustein(baustein)
                                    }}
                                    className="p-2 rounded-full transition-colors text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40"
                                    title="Baustein clonen"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleDeleteBaustein(baustein)
                                    }}
                                    disabled={baustein.Eigentuemer !== 'alle' && baustein.Eigentuemer !== currentUsername}
                                    className={`p-2 rounded-full transition-colors ${
                                      baustein.Eigentuemer !== 'alle' && baustein.Eigentuemer !== currentUsername
                                        ? 'text-gray-300 cursor-not-allowed'
                                        : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                                    }`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })
                      ) : (
                        (pinFilter === 'pins' || searchTerm.trim() !== '' || currentSubfolders.length === 0) && (
                          <EmptyTableRow
                            colSpan={10}
                            title={searchTerm ? 'Keine Bausteine gefunden' : 'Keine Bausteine verfügbar'}
                            description={searchTerm ? 'Keine Treffer für die aktuelle Suche.' : undefined}
                          />
                        )
                      )}
                    </TableBody>
                    </Table>
                  </TableScrollArea>
                </>
              )}
            </div>
          </div>
        )}


        {/* Panel B: Baustein Details und Lernpläne */}
        {panelType === 'B' && selectedBaustein && (() => {
          const bPerf = performanceStats?.bausteine.find(p => p.BID === selectedBaustein.BID)
          const competencyCount = selectedBaustein.COMPETENCY_COUNT ?? 0
          const assignedClassCount = zuordnungen.length
          const lernplanCount = sortedLernplaene.length

          return (
          <div className="space-y-4">
            {bPerf && (bPerf.access_count > 0 || bPerf.student_count > 0) && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="info">
                  <Users className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  {bPerf.access_count > 0
                    ? `${bPerf.student_count}/${bPerf.access_count} Schüler`
                    : `${bPerf.student_count} Schüler`}
                </Badge>
                <Badge variant="success">
                  <BarChart2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  Ø {Number(bPerf.avg_completion_pct).toFixed(1)}%
                </Badge>
              </div>
            )}

            <CollapsibleTile
              title="Stammdaten"
              defaultOpen
              contentClassName="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className={bbLabelClass}>Name *</label>
                    <EditableText
                      value={selectedBaustein.NAME || ''}
                      onSave={(value) => handleUpdateBaustein('NAME', value)}
                      placeholder="Baustein Name"
                      className={bbInputClass}
                    />
                  </div>
                  <div>
                    <label className={bbLabelClass}>Fach *</label>
                    <EditableSelect
                      value={selectedBaustein.FACH || ''}
                      onSave={(value) => handleUpdateBaustein('FACH', value)}
                      options={[
                        { value: 'Deutsch', label: 'Deutsch' },
                        { value: 'Mathematik', label: 'Mathematik' },
                        { value: 'Englisch', label: 'Englisch' },
                        { value: 'Französisch', label: 'Französisch' },
                        { value: 'Biologie', label: 'Biologie' },
                        { value: 'Chemie', label: 'Chemie' },
                        { value: 'Physik', label: 'Physik' },
                        { value: 'Geschichte', label: 'Geschichte' },
                        { value: 'Erdkunde', label: 'Erdkunde' },
                        { value: 'Politik', label: 'Politik' },
                        { value: 'Religion', label: 'Religion' },
                        { value: 'Kunst', label: 'Kunst' },
                        { value: 'Musik', label: 'Musik' },
                        { value: 'Sport', label: 'Sport' }
                      ]}
                      placeholder="Fach wählen"
                      className={bbInputClass}
                    />
                  </div>
                  <div>
                    <label className={bbLabelClass}>Stufe *</label>
                    <EditableSelect
                      value={selectedBaustein.STUFE || ''}
                      onSave={(value) => handleUpdateBaustein('STUFE', value)}
                      options={[
                        { value: '5', label: '5. Klasse' },
                        { value: '6', label: '6. Klasse' },
                        { value: '7', label: '7. Klasse' },
                        { value: '8', label: '8. Klasse' },
                        { value: '9', label: '9. Klasse' },
                        { value: '10', label: '10. Klasse' }
                      ]}
                      placeholder="Stufe wählen"
                      className={bbInputClass}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={bbLabelClass}>Jahr *</label>
                    <EditableNumber
                      value={selectedBaustein.JAHR ? Number(selectedBaustein.JAHR) : null}
                      onSave={(value) => handleUpdateBaustein('JAHR', value)}
                      placeholder="Jahr eingeben"
                      min={2020}
                      max={2030}
                      className={bbInputClass}
                    />
                  </div>
                  <div>
                    <label className={bbLabelClass}>Start Datum *</label>
                    <EditableDate
                      value={selectedBaustein.START_DATUM || ''}
                      onSave={(value) => handleUpdateBausteinDate('START_DATUM', value)}
                      placeholder="Start Datum"
                      className={bbInputClass}
                    />
                  </div>
                  <div>
                    <label className={bbLabelClass}>End Datum *</label>
                    <EditableDate
                      value={selectedBaustein.END_DATUM || ''}
                      onSave={(value) => handleUpdateBausteinDate('END_DATUM', value)}
                      placeholder="End Datum"
                      className={bbInputClass}
                    />
                  </div>
                </div>
              </div>

              {/* Info Field */}
              <div className="mt-6">
                <label className={bbLabelClass}>Info</label>
                <EditableText
                  value={selectedBaustein.INFO || ''}
                  onSave={(value) => handleUpdateBaustein('INFO', value)}
                  placeholder="Zusätzliche Informationen zum Baustein..."
                  multiline={true}
                  className={`${bbInputClass} min-h-[60px]`}
                />
              </div>

              {/* Schützen Toggle */}
              <div className="mt-6">
                <div className={bbMutedPanelClass}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Schützen</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedBaustein.Eigentuemer === 'alle' 
                          ? 'Baustein ist für alle Benutzer verfügbar'
                          : `Baustein ist nur für ${selectedBaustein.Eigentuemer} verfügbar`
                        }
                      </p>
                    </div>
                    <ToggleSwitch
                      label="Schützen"
                      value={selectedBaustein.Eigentuemer !== 'alle'}
                      onToggle={async (checked) => {
                        const newEigentuemer = checked ? currentUsername : 'alle'
                        await handleUpdateBaustein('Eigentuemer', newEigentuemer)
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Schülerkompetenz Update */}
              <div className="mt-6">
                <div className={bbAccentBluePanel}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Schülerkompetenzen aktualisieren</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Aktualisiert die Kompetenznachweise für alle Schüler, die mit diesem Baustein verknüpft sind
                      </p>
                      {updatingCompetencies && progressData && (
                        <div className="mt-3 space-y-2">
                          {/* Progress Bar */}
                          <div className="w-full bg-blue-200 dark:bg-blue-900 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{
                                width: progressData.totalBelege > 0
                                  ? `${(progressData.processedBelege / progressData.totalBelege) * 100}%`
                                  : '0%'
                              }}
                            />
                          </div>
                          {/* Statistics */}
                          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Belege:</span>
                              <span className="ml-1 font-semibold text-gray-900 dark:text-gray-100">
                                {progressData.processedBelege} / {progressData.totalBelege}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Aktualisiert:</span>
                              <span className="ml-1 font-semibold text-green-700">
                                {progressData.updatedBelege}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Erstellt:</span>
                              <span className="ml-1 font-semibold text-blue-700">
                                {progressData.totalEvidenceCreated}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Entfernt:</span>
                              <span className="ml-1 font-semibold text-orange-700">
                                {progressData.totalEvidenceRemoved}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Skillmap:</span>
                              <span className="ml-1 font-semibold text-purple-700">
                                {progressData.totalStatusUpdates}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Initialisiert:</span>
                              <span className="ml-1 font-semibold text-indigo-700">
                                {progressData.totalSkillmapInitialized || 0}
                              </span>
                            </div>
                          </div>
                          {progressData.errors > 0 && (
                            <div className="text-xs text-red-600">
                              ⚠️ {progressData.errors} Fehler aufgetreten
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={handleUpdateStudentCompetencies}
                        disabled={updatingCompetencies}
                        className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors ${
                          updatingCompetencies
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {updatingCompetencies ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Wird aktualisiert...</span>
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4" />
                            <span>Schülerkompetenz update</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleTile>

            <CollapsibleTile
              title={
                <>
                  <Target className="h-4 w-4 text-blue-600" aria-hidden />
                  Curricula & Kompetenzen
                </>
              }
              defaultOpen={false}
              headerActions={
                <>
                  {competencyCount > 0 && (
                    <Badge variant="info">{competencyCount} Kompetenz{competencyCount !== 1 ? 'en' : ''}</Badge>
                  )}
                  {selectedCurriculumIds.length > 0 && (
                    <Badge variant="neutral">{selectedCurriculumIds.length} Curriculum{selectedCurriculumIds.length !== 1 ? 'a' : ''}</Badge>
                  )}
                  {savingCurricula && (
                    <span className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      Speichere…
                    </span>
                  )}
                </>
              }
              contentClassName="space-y-6"
            >
                  {/* Curriculum-Auswahl (Verbessertes Card-Design) */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Curricula zuordnen
                    </label>
                      {selectedCurriculumIds.length > 0 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded-full border border-gray-200 dark:border-gray-600">
                          {selectedCurriculumIds.length} ausgewählt
                        </span>
                      )}
                    </div>
                    
                    {!showCurriculumSelect ? (
                      <button
                        type="button"
                        onClick={() => setShowCurriculumSelect(true)}
                        className={bbCurriculumPickerBtn}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg group-hover:bg-blue-200 dark:group-hover:bg-blue-800/60 transition-colors">
                            <Plus className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 block">
                          {selectedCurriculumIds.length === 0 
                            ? 'Curricula auswählen...' 
                                : `${selectedCurriculumIds.length} Curriculum${selectedCurriculumIds.length !== 1 ? 'a' : ''} zugeordnet`
                          }
                        </span>
                            {selectedCurriculumIds.length > 0 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Klicken zum Bearbeiten
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronDown className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                      </button>
                    ) : (
                      <div className={bbCurriculumDropdown}>
                        {/* Header mit Suche */}
                        <div className={bbCurriculumDropdownHeader}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Curricula auswählen</span>
                          <button
                            type="button"
                              onClick={() => {
                                setShowCurriculumSelect(false)
                                setCurriculumSearchTerm('')
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                            >
                              <Check className="h-4 w-4" />
                            Fertig
                          </button>
                        </div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Curricula durchsuchen..."
                              value={curriculumSearchTerm}
                              onChange={(e) => setCurriculumSearchTerm(e.target.value)}
                              className={`${bbInputClass} pl-10 pr-4`}
                            />
                          </div>
                        </div>
                        
                        {/* Curriculum-Liste */}
                        <div className="max-h-96 overflow-y-auto p-4">
                          {loadingCurricula ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                              <span className="ml-2 text-sm text-gray-600">Lade Curricula...</span>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {curricula
                                .filter(curriculum => {
                                  if (!curriculumSearchTerm) return true
                                  const searchLower = curriculumSearchTerm.toLowerCase()
                                  const curriculumId = getCurriculumId(curriculum)
                                  const name = (curriculum.name || '').toLowerCase()
                                  const subject = (curriculum.subject || '').toLowerCase()
                                  const cid = (curriculum.cid || '').toLowerCase()
                                  return name.includes(searchLower) || 
                                         subject.includes(searchLower) || 
                                         cid.includes(searchLower) ||
                                         curriculumId.toString().includes(searchLower)
                                })
                                .map(curriculum => {
                            const curriculumId = getCurriculumId(curriculum)
                            if (!curriculumId) {
                              return null
                            }
                                  const isSelected = selectedCurriculumIds.includes(curriculumId)
                                  const competencies = curriculumCompetencies[curriculumId] || []
                                  const isLoadingComps = loadingCompetencies[curriculumId]
                                  
                            return (
                                    <div
                                key={curriculumId}
                                      className={`relative border-2 rounded-lg p-4 transition-all duration-200 cursor-pointer ${
                                        isSelected
                                          ? 'border-blue-500 bg-blue-50 shadow-md dark:border-blue-400 dark:bg-blue-950/50'
                                          : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm dark:border-gray-600 dark:bg-gray-900 dark:hover:border-blue-500'
                                      }`}
                                      onClick={() => {
                                        console.log('🎯 Curriculum-Card geklickt', { curriculumId, isSelected, selectedBaustein: selectedBaustein?.BID })
                                        if (!selectedBaustein) {
                                          console.error('❌ selectedBaustein ist nicht gesetzt beim Click!')
                                          return
                                        }
                                        if (isSelected) {
                                          console.log('➖ Entferne Curriculum', curriculumId)
                                      updateCurriculumSelection(prev => prev.filter(id => id !== curriculumId))
                                        } else {
                                          console.log('➕ Füge Curriculum hinzu', curriculumId)
                                          updateCurriculumSelection(prev => [...prev, curriculumId])
                                          fetchCurriculumCompetencies(curriculumId)
                                        }
                                      }}
                                    >
                                      {/* Checkbox */}
                                      <div className="absolute top-3 right-3">
                                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                                          isSelected
                                            ? 'border-blue-500 bg-blue-500'
                                            : 'border-gray-300 bg-white dark:border-gray-500 dark:bg-gray-800'
                                        }`}>
                                          {isSelected && <Check className="h-4 w-4 text-white" />}
                                        </div>
                                      </div>
                                      
                                      {/* Curriculum Info */}
                                      <div className="pr-8">
                                        <h4 className="font-semibold text-gray-900 text-sm mb-1">
                                          {curriculum.name || `Curriculum ${curriculumId}`}
                                        </h4>
                                        <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                                          {curriculum.subject && (
                                            <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                                              {curriculum.subject}
                                </span>
                                          )}
                                          {curriculum.exam_type && (
                                            <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                                              {curriculum.exam_type}
                                            </span>
                                          )}
                                        </div>
                                        
                                        {/* Kompetenzen-Anzeige */}
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                          {isSelected && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                if (competencies.length === 0 && !isLoadingComps) {
                                                  fetchCurriculumCompetencies(curriculumId)
                                                }
                                              }}
                                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                            >
                                              {isLoadingComps ? (
                                                <>
                                                  <Loader2 className="h-3 w-3 animate-spin" />
                                                  Lade Kompetenzen...
                                                </>
                                              ) : competencies.length > 0 ? (
                                                <>
                                                  <Target className="h-3 w-3" />
                                                  {competencies.length} Kompetenz{competencies.length !== 1 ? 'en' : ''}
                                                </>
                                              ) : (
                                                <>
                                                  <Target className="h-3 w-3" />
                                                  Kompetenzen anzeigen
                                                </>
                                              )}
                                            </button>
                                          )}
                                          
                                          {/* Kompetenzen-Liste */}
                                          {competencies.length > 0 && (
                                            <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                                              {competencies.slice(0, 5).map((comp: any) => (
                                                <div
                                                  key={comp.id}
                                                  className="text-xs text-gray-600 dark:text-gray-300 px-2 py-1 bg-white dark:bg-gray-800 rounded border border-gray-100 dark:border-gray-600"
                                                >
                                                  {comp.name || `Kompetenz ${comp.id}`}
                                                </div>
                                              ))}
                                              {competencies.length > 5 && (
                                                <div className="text-xs text-gray-500 italic px-2">
                                                  +{competencies.length - 5} weitere...
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                          })}
                        </div>
                          )}
                          
                          {curricula.filter(c => {
                            if (!curriculumSearchTerm) return true
                            const searchLower = curriculumSearchTerm.toLowerCase()
                            const curriculumId = getCurriculumId(c)
                            const name = (c.name || '').toLowerCase()
                            const subject = (c.subject || '').toLowerCase()
                            return name.includes(searchLower) || subject.includes(searchLower)
                          }).length === 0 && !loadingCurricula && (
                            <div className="text-center py-8 text-gray-500 text-sm">
                              Keine Curricula gefunden
                      </div>
                    )}
                        </div>
                      </div>
                    )}
                    
                    {/* Ausgewählte Curricula als Chips */}
                    {selectedCurriculumIds.length > 0 && !showCurriculumSelect && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {selectedCurriculumIds.map(curriculumId => {
                          const curriculum = curricula.find(c => getCurriculumId(c) === curriculumId)
                          const competencies = curriculumCompetencies[curriculumId] || []
                          return (
                            <div
                              key={curriculumId}
                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 border border-blue-300 rounded-lg text-sm group dark:bg-blue-950/40 dark:border-blue-700"
                            >
                              <Target className="h-4 w-4 text-blue-600" />
                              <span className="font-medium text-blue-900 dark:text-blue-100">
                              {curriculum?.name || `ID: ${curriculumId}`}
                              </span>
                              {competencies.length > 0 && (
                                <span className="text-xs text-blue-700 bg-blue-200 px-2 py-0.5 rounded-full">
                                  {competencies.length}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  updateCurriculumSelection(prev => prev.filter(id => id !== curriculumId))
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-blue-700 hover:text-blue-900"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {/* CompetencySelector */}
                  <CompetencySelector
                    entityType="building_block"
                    entityId={selectedBaustein.BID}
                    curriculumIds={selectedCurriculumIds}
                    onUpdate={handleBuildingBlockCompetencyUpdate}
                    requireCurriculumForAutoAssign
                  />
                  <div className={bbDashedBluePanel}>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <label className="inline-flex items-center gap-2 text-sm text-blue-900 dark:text-blue-100">
                        <input
                          type="checkbox"
                          className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                          checked={topDownEnabled}
                          onChange={(event) => setTopDownEnabled(event.target.checked)}
                        />
                        <span>Top-Down Fallback für leere Lernpläne & Aufgaben</span>
                      </label>
                      <button
                        type="button"
                        onClick={handleTopDownSync}
                        disabled={!topDownEnabled || topDownSyncing}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {topDownSyncing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Verteilt...
                          </>
                        ) : (
                          <>
                            <ArrowDown className="h-4 w-4" />
                            Kompetenzen verteilen
                          </>
                        )}
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-blue-800 dark:text-blue-200">
                      Nutzt alle Kompetenzen des Bausteins und weist sie Lernplänen und Aufgaben zu, die bislang keine eigenen Zuordnungen besitzen.
                    </p>
                  </div>

                  {/* Alle Kompetenzen entfernen */}
                  <div className={bbDashedRedPanel}>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-sm text-red-900 dark:text-red-200">
                        Alle Kompetenzen rekursiv entfernen (Baustein + Lernpläne + Aufgaben)
                      </span>
                      <button
                        type="button"
                        onClick={handleRemoveAllCompetenciesFromBuildingBlock}
                        disabled={removingAllCompetencies}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {removingAllCompetencies ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Entferne...
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4" />
                            Alle entfernen
                          </>
                        )}
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-red-800 dark:text-red-300">
                      ⚠️ Entfernt ALLE Kompetenz-Zuordnungen des Bausteins, seiner Lernpläne und aller Aufgaben. Diese Aktion kann nicht rückgängig gemacht werden!
                    </p>
                  </div>
            </CollapsibleTile>

            <CollapsibleTile
              title={
                <>
                  <Users className="h-4 w-4 text-blue-600" aria-hidden />
                  Klassen-Zuordnung
                </>
              }
              defaultOpen={false}
              headerActions={
                assignedClassCount > 0 ? (
                  <Badge variant="info">
                    {assignedClassCount} von {klassen.length} Klasse{klassen.length !== 1 ? 'n' : ''}
                  </Badge>
                ) : klassen.length > 0 ? (
                  <Badge variant="neutral">Keine zugeordnet</Badge>
                ) : null
              }
            >
                  {loadingKlassen ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                      <span className="ml-2 text-gray-600">Lade Klassen...</span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Wählen Sie die Klassen aus, für die dieser Baustein verfügbar sein soll:
                      </p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {klassen.map((klasse) => {
                          const isAssigned = zuordnungen.some(z => z.Klasse === klasse.Klasse)
                          return (
                            <label
                              key={klasse.Klasse}
                              className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                isAssigned
                                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950/40 dark:text-blue-200'
                                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-gray-500 dark:hover:bg-gray-800'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isAssigned}
                                onChange={(e) => handleClassAssignment(klasse.Klasse, e.target.checked)}
                                className="sr-only"
                              />
                              <div className="flex items-center w-full">
                                <div className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center ${
                                  isAssigned
                                    ? 'border-blue-500 bg-blue-500'
                                    : 'border-gray-300'
                                }`}>
                                  {isAssigned && (
                                    <Check className="h-3 w-3 text-white" />
                                  )}
                                </div>
                                <span className="text-sm font-medium">
                                  {klasse.Klasse}
                                </span>
                              </div>
                            </label>
                          )
                        })}
                      </div>
                      
                      {klassen.length === 0 && (
                        <div className="text-center py-4 text-gray-500">
                          <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p>Keine Klassen verfügbar</p>
                        </div>
                      )}
                    </div>
                  )}
            </CollapsibleTile>

            <CollapsibleTile
              title="Lernpläne"
              defaultOpen
              headerActions={
                <>
                  {lernplanCount > 0 && (
                    <Badge variant="info">{lernplanCount} Lernplan{lernplanCount !== 1 ? 'e' : ''}</Badge>
                  )}
                  <Button type="button" size="sm" onClick={handleCreateLernplan}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Hinzufügen
                  </Button>
                </>
              }
              contentClassName="space-y-3"
            >
                  {loadingLernplaene ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-500">Lade Lernpläne...</p>
                    </div>
                  ) : sortedLernplaene.length > 0 ? (
                    <div className="space-y-3">
                      <div className="mb-3 text-sm text-gray-600 dark:text-gray-300 bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-950/30 dark:border-blue-800">
                        <p>💡 <strong>Drag & Drop:</strong> Ziehen Sie die Lernpläne per Drag & Drop, um die Reihenfolge zu ändern. Die Sortierung wird automatisch in der Datenbank gespeichert.</p>
                      </div>
                      
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleLernplanDragEnd}
                      >
                        <SortableContext
                          items={sortedLernplaene.map((lernplan) => String(lernplan.LPID))}
                          strategy={verticalListSortingStrategy}
                        >
                          {sortedLernplaene.map((lernplan) => (
                            <SortableLernplan 
                              key={lernplan.LPID} 
                              lernplan={lernplan} 
                              onEdit={(lernplan) => handleEditLernplan(lernplan.LPID)}
                              onUpdateSortierung={async (lpid, newSortierung) => {
                                // This is handled by the drag end handler
                              }}
                              onExport={(lpid) => handleExportLernplan(lpid)}
                              onExportSimple={(lpid) => handleExportLernplanSimple(lpid)}
                              onUpdateLernplan={handleUpdateLernplan}
                              onDelete={handleDeleteLernplan}
                              onClone={handleCloneLernplan}
                              perfStats={performanceStats?.lernplaene.find(p => String(p.LPID) === String(lernplan.LPID)) ?? null}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <p>Keine Lernpläne für diesen Baustein gefunden.</p>
                    </div>
                  )}
            </CollapsibleTile>
          </div>
          )
        })()}

        {/* Panel C: Lernplan bearbeiten */}
        {panelType === 'C' && selectedLernplanForEdit && (
          <div className="card overflow-hidden">
            {/* Farbiger Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Lernplan bearbeiten
                  </h2>
                  <p className="text-blue-100 text-sm mt-1">
                    {selectedLernplanForEdit.NAME || 'Unbenannter Lernplan'}
                  </p>
                </div>
                <button
                  onClick={() => setPanelType('B')}
                  className="flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                >
                  <X className="h-4 w-4 mr-2" />
                  Zurück zu Lernplänen
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Wichtige Felder */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className={bbLabelClass}>
                      Name *
                    </label>
                    <input
                      type="text"
                      value={editLernplanForm.NAME || ''}
                      onChange={(e) => handleLernplanInputChange('NAME', e.target.value)}
                      className={bbInputClass}
                      placeholder="Name des Lernplans"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className={bbLabelClass}>
                      Start Datum *
                    </label>
                    <input
                      type="date"
                      value={editLernplanForm.START_DATUM || ''}
                      onChange={(e) => handleLernplanInputChange('START_DATUM', e.target.value)}
                      max={editLernplanForm.END_DATUM || undefined}
                      className={bbInputClass}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className={bbLabelClass}>
                      Differenzierung
                    </label>
                    <select
                      value={editLernplanForm.Diff || 'GK'}
                      onChange={(e) => handleLernplanInputChange('Diff', e.target.value)}
                      className={bbInputClass}
                    >
                      <option value="GK">Grundkurs (GK)</option>
                      <option value="EK">Erweiterungskurs (EK)</option>
                      <option value="FK">Förderkurs (FK)</option>
                      <option value="WK">DAZ (WK)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className={bbLabelClass}>
                      End Datum *
                    </label>
                    <input
                      type="date"
                      value={editLernplanForm.END_DATUM || ''}
                      onChange={(e) => handleLernplanInputChange('END_DATUM', e.target.value)}
                      min={editLernplanForm.START_DATUM || undefined}
                      className={bbInputClass}
                      required
                    />
                  </div>
                </div>
              </div>
              
              {/* Lernziele */}
              <div className="rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-4 dark:border-green-900 dark:from-green-950/40 dark:to-emerald-950/40">
                <label className="block text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                  🎯 Lernziele
                </label>
                <textarea
                  value={editLernplanForm.LERNZIELE || ''}
                  onChange={(e) => handleLernplanInputChange('LERNZIELE', e.target.value)}
                  className={bbTextareaClass}
                  placeholder="Beschreiben Sie die Lernziele für diesen Lernplan..."
                />
              </div>

              {/* Kompetenz-Zuordnung für Lernplan */}
              <div className={bbMutedPanelClass}>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <Target className="h-5 w-5 mr-2 text-blue-600" />
                  Kompetenzen
                </h3>
                
                {/* Info über geerbte Curricula */}
                {selectedCurriculumIds.length === 0 && (
                  <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-950/30">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      ⚠️ Bitte wählen Sie im Baustein ein Curriculum aus, um Auto-Assign zu nutzen.
                    </p>
                  </div>
                )}

                {/* CompetencySelector */}
                {selectedLernplanForEdit?.LPID && (
                  <CompetencySelector
                    entityType="learning_plan"
                    entityId={Number(selectedLernplanForEdit.LPID)}
                    curriculumIds={selectedCurriculumIds}
                    onUpdate={() => {}}
                    requireCurriculumForAutoAssign={false}
                  />
                )}

                <div className={bbDashedBluePanel}>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <label className="inline-flex items-center gap-2 text-sm text-blue-900 dark:text-blue-100">
                      <input
                        type="checkbox"
                        className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                        checked={learningPlanTopDownEnabled}
                        onChange={(event) => setLearningPlanTopDownEnabled(event.target.checked)}
                      />
                      <span>Top-Down Fallback für leere Aufgaben</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleLearningPlanTopDownSync}
                      disabled={!learningPlanTopDownEnabled || learningPlanTopDownSyncing}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {learningPlanTopDownSyncing ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Verteilt...
                        </>
                      ) : (
                        <>
                          <ArrowDown className="h-4 w-4" />
                          Kompetenzen verteilen
                        </>
                      )}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-blue-800 dark:text-blue-200">
                    Nutzt alle Kompetenzen des Lernplans und weist sie Aufgaben zu, die bislang keine eigenen Zuordnungen besitzen.
                  </p>
                </div>

                {/* Alle Kompetenzen entfernen */}
                <div className={bbDashedRedPanel}>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm text-red-900 dark:text-red-200">
                      Alle Kompetenzen rekursiv entfernen (Lernplan + Aufgaben)
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveAllCompetenciesFromLearningPlan}
                      disabled={removingLernplanCompetencies}
                      className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {removingLernplanCompetencies ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Entferne...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4" />
                          Alle entfernen
                        </>
                      )}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-red-800 dark:text-red-300">
                    ⚠️ Entfernt ALLE Kompetenz-Zuordnungen dieses Lernplans und aller zugehörigen Aufgaben. Diese Aktion kann nicht rückgängig gemacht werden!
                  </p>
                </div>
              </div>

              {/* Aufgaben-Liste */}
              <div className={bbTasksSection}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-purple-800 dark:text-purple-200">
                    📋 Aufgaben ({tasks.length})
                  </h3>
                  {updatingTaskOrder && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                      <span>Reihenfolge wird aktualisiert...</span>
                    </div>
                  )}
                </div>
                
                {loadingTasks ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">Lade Aufgaben...</p>
                  </div>
                ) : tasks.length > 0 ? (
                  <div className={bbMutedPanelClass}>
                    <div className={bbInfoBlueBox}>
                      <p>💡 <strong>Drag & Drop:</strong> Ziehen Sie die Aufgaben per Drag & Drop, um die Reihenfolge zu ändern. Die Sortierung wird automatisch in der Datenbank gespeichert.</p>
                    </div>
                    
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={tasks.map(task => task.AID)}
                        strategy={verticalListSortingStrategy}
                      >
                                           {tasks.map((task) => (
                     <SortableTask key={task.AID} task={task} onUpdateTask={updateTaskField} onDeleteTask={deleteTask} deletingTask={deletingTask} onShowCompetencies={handleShowTaskCompetencies} competencyRefreshKey={competencyRefreshKey} perfStats={performanceStats?.aufgaben.find(p => p.AID === task.AID) ?? null} />
                   ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4">
                    <p>Keine Aufgaben für diesen Lernplan gefunden.</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                <button
                  onClick={createNewTask}
                  disabled={creatingTask}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg font-medium flex items-center space-x-2"
                >
                  {creatingTask ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Erstellen...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      <span>Neue Aufgabe</span>
                    </>
                  )}
                </button>
                
                <div className="flex space-x-3">
                  <button
                    onClick={handleBackToPanelB}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleSaveLernplan}
                    disabled={savingLernplan}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {savingLernplan ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Speichern...</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        <span>Speichern</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Competency Modal */}
        {showTaskCompetencyModal && selectedTaskForCompetency && (
          <TaskCompetencyModal
            isOpen={showTaskCompetencyModal}
            onClose={() => {
              setShowTaskCompetencyModal(false)
              setSelectedTaskForCompetency(null)
              setCompetencyRefreshKey(prev => prev + 1) // Trigger refresh
            }}
            taskId={selectedTaskForCompetency}
            taskTitle={tasks.find(t => t.AID === selectedTaskForCompetency)?.Kurzberzeichnung || 'Aufgabe'}
            curriculumIds={selectedCurriculumIds}
            onUpdate={() => setCompetencyRefreshKey(prev => prev + 1)}
          />
        )}
    </div>
  )
}